// src/pages/trading-portal/TradeExecution/AIAgentPage.js
// AI Trading Agent Management - 4-tab interface: Agent Management, Prompt Editor, Decision Log, Performance

import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Drawer,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Skeleton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Grid,
    Card,
    CardContent,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Checkbox,
    Switch,
    Slider,
    Collapse,
    Menu,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import { saveAs } from 'file-saver';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import { createChart, ColorType, CandlestickSeries, HistogramSeries } from 'lightweight-charts';
import ConnectAccountTab from './ConnectAccountTab';
import AIAgentRuleEditor from './AIAgentRuleEditor';
import AIAgentDataSimulator from './AIAgentDataSimulator';
import {
    buildMarketDataSections, buildVariableValueLookup, compactVariableValue, CompactMarketDataSections,
    DataFieldRow, formatFieldValue, isNAFieldValue, joinCandleText, MarketDataSectionGrid, resolveEnabledAgents,
} from './MarketDataSections';
import {
    AIAgentStatus, AIAgentStatusLabels, AIAgentStatusColors,
    AIAgentDecisionAction, AIAgentDecisionActionLabels, AIAgentDecisionActionColors,
    CycleStatus, CycleStatusLabels, CycleStatusColors,
    PromptVersionStatus, PromptVersionStatusLabels, PromptVersionStatusColors,
    TimeFrame, TimeFrameLabels,
} from '../stockTradingEnums';
import { openLiveMarketDataStream, timeframeToSeconds } from '../../../services/streamingMarketData';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

// Timeframe ↔ seconds mapping. Used by `secondsToTimeframe` as a fallback when
// editing legacy agents that pre-date the Decision Timing split (no
// `config_json.timeframe` set). New agents store `timeframe` and
// `cycle_interval_seconds` independently; the scheduler / Live Market Data
// auto-refresh use seconds (Decision Timing) and `_gather_market_data` uses
// the explicit timeframe for candle/VWAP/ATR calculations.
const TIMEFRAME_SECONDS = {
    ONE_MIN: 60,
    TWO_MIN: 120,
    THREE_MIN: 180,
    FOUR_MIN: 240,
    FIVE_MIN: 300,
    FIFTEEN_MIN: 900,
    ONE_HOUR: 3600,
    TWO_HOUR: 7200,
    FOUR_HOUR: 14400,
    FIVE_HOUR: 18000,
    DAILY: 86400,
    WEEKLY: 604800,
    MONTHLY: 2592000,
};
const secondsToTimeframe = (secs) => {
    const s = Number(secs);
    const match = Object.entries(TIMEFRAME_SECONDS).find(([, v]) => v === s);
    return match ? match[0] : 'FIVE_MIN';
};

// The daily cycle limit is derived, not configured. This previews what the backend
// enforces (session_cycle_limit in backend/utils/AgentTradingSchedule.py): one cycle
// at the open and one per step until the window closes, stepping at the faster of
// Decision Timing and the Timeframe's bar. Keep the two in step.
const estimateDailyCycles = (form) => {
    const toMinutes = (hhmm) => {
        const match = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
        return match ? Number(match[1]) * 60 + Number(match[2]) : null;
    };
    const open = toMinutes(form.trading_start_time);
    const close = toMinutes(form.trading_end_time);
    // A blank Decision Timing is saved as 30s (handleCreateAgent).
    const interval = Number(form.cycle_interval_seconds) > 0 ? Number(form.cycle_interval_seconds) : 30;
    const bar = form.timeframe ? timeframeToSeconds(form.timeframe) : null;
    const byTimeframe = Boolean(bar) && bar <= interval;
    const cadence = byTimeframe ? bar : interval;
    // Blank start or end is 24/7. The closing minute trades too: 15:00 runs to 15:00:59.
    const windowSeconds = open === null || close === null
        ? 24 * 3600
        : (((close - open + 1440) % 1440) + 1) * 60;
    return { cycles: Math.floor((windowSeconds - 1) / cadence) + 1, cadence, byTimeframe };
};

const formatCadence = (seconds) => {
    if (seconds % 3600 === 0) return `${seconds / 3600} h`;
    if (seconds % 60 === 0) return `${seconds / 60} min`;
    return `${seconds} s`;
};

// Read-only stand-in for the old Max Daily Cycles field in the agent dialog.
const DailyCyclesNote = ({ form }) => {
    const { cycles, cadence, byTimeframe } = estimateDailyCycles(form);
    const hasWindow = Boolean(form.trading_start_time && form.trading_end_time);
    // A crypto pair is the broker's "BASE/QUOTE" form and trades every day; anything
    // else is a stock (utils/AgentTradingSchedule.py trades_every_day).
    const isStock = Boolean(form.symbol) && !String(form.symbol).includes('/');
    return (
        <Box sx={{ p: 1.5, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                Max Daily Cycles: {cycles.toLocaleString()} (automatic)
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b' }}>
                One cycle every {formatCadence(cadence)}
                {hasWindow ? ` from ${form.trading_start_time} to ${form.trading_end_time}` : ' around the clock'}
                {byTimeframe ? ` (${TimeFrameLabels[form.timeframe]} Timeframe)` : ' (Decision Timing)'}.
            </Typography>
            {isStock && (
                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b' }}>
                    Stocks trade Monday to Friday: no cycles run on Saturday or Sunday (Chicago time), even inside the date range.
                </Typography>
            )}
        </Box>
    );
};

// Under an agent's status chip on a Saturday or Sunday: a stock agent does not run then,
// even while ACTIVE (`weekend_closed`, judged on Chicago time, the US market's clock).
// Nothing on other days.
const WeekendCaption = ({ agent }) => (agent.weekend_closed ? (
    <Typography sx={{ fontFamily, fontSize: '0.7rem', mt: 0.25, color: '#b45309' }}>
        Closed {agent.weekend_closed} — stocks trade Monday to Friday
    </Typography>
) : null);

// Under an agent's status chip: where its Trading Start / End Date range stands
// (`trading_dates_state`, computed in the agent's timezone). Nothing without dates.
const TradingDatesCaption = ({ agent }) => {
    const state = agent.trading_dates_state;
    if (!state) return null;
    let text = agent.trading_end_date ? `Until ${agent.trading_end_date}` : `From ${agent.trading_start_date}`;
    if (state === 'NOT_STARTED') text = `Starts ${agent.trading_start_date}`;
    if (state === 'ENDED') text = `Ended ${agent.trading_end_date}`;
    return (
        <Typography sx={{ fontFamily, fontSize: '0.7rem', mt: 0.25, color: state === 'ENDED' ? '#ef4444' : '#64748b' }}>
            {text}
        </Typography>
    );
};

const headCellSx = {
    fontFamily,
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const bodyCellSx = { fontFamily, fontSize: '0.85rem', color: '#334155' };

// Default Martingale ladder used by the LLM data snapshot's sizing_t1..t4_*
// fields. Mirrors backend DEFAULT_POSITION_SIZING in AIAgentController.py — keep
// the two in sync if the platform-wide default ever changes.
const DEFAULT_POSITION_SIZING = { divisor: 15, multipliers: [1, 2, 4, 8] };

const INITIAL_AGENT_FORM = {
    name: '',
    description: '',
    execution_mode: 'NO_LLM',
    symbol: '',
    symbol_id: '',
    broker_connection_id: '',
    temperature: 0.3,
    max_output_tokens: 12000,
    timeframe: 'FIVE_MIN',
    // '' means "same as Timeframe" — the field is optional and absent from
    // config_json unless the user picks a specific VWAP timeframe.
    vwap_timeframe: '',
    cycle_interval_seconds: 30,
    auto_execute: false,
    capital_allocation: '',
    max_position_value: '',
    max_daily_loss: '',
    max_open_positions: 3,
    trading_start_time: '',
    trading_end_time: '',
    trading_timezone: 'America/Chicago',
    // Optional 'YYYY-MM-DD' bounds on the days the agent trades; '' means no bound.
    trading_start_date: '',
    trading_end_date: '',
    // Per-side order type sent to the broker. LIMIT is the default on both
    // sides because it matches how agents behaved before this was configurable:
    // a decision that carries a price goes out as a limit order, one that
    // doesn't goes out at the market.
    order_types: { buy: 'LIMIT', sell: 'LIMIT' },
    // Open Order Cancellation: off by default, so an order waits at the broker until it
    // fills, is cancelled or expires. Backend: ORDER_CANCELLATION_MINUTES.
    order_cancellation: { enabled: false, after_minutes: 30 },
    enabled_agents: {
        macro_strategist: true,
        options_volatility: true,
        asset_strategist: true,
    },
};

// The waits Open Order Cancellation offers, in minutes (backend ORDER_CANCELLATION_MINUTES).
const ORDER_CANCELLATION_MINUTES = [30, 60, 120, 180];

// ==================== PROMPT BUILDER VARIABLE CATALOG ====================
// Mirrors backend DEFAULT_DATA_TEMPLATE (backend/controller/StockTrading/AIAgentDefaultPrompt.py).
// Each variable can be dragged/clicked into the System Prompt (inserts as
// `name` — backticked reference) or the Data Template (inserts as {name} — the
// str.format placeholder the backend substitutes at cycle time).
//
// Variable `agent` tag: when set, the variable is hidden from the palette if
// that strategist agent is disabled on the selected agent
// (config_json.enabled_agents). Untagged variables are supporting/core fields
// and are always shown (VWAP execution, portfolio, snapshot, etc.).
// Tab indices are named because they are positional and a tab inserted in the
// middle shifts every one after it - `activeTab === 4` scattered through the
// file is exactly how that goes wrong silently.
const TAB = {
    AGENTS: 0,
    CONNECT: 1,
    PROMPTS: 2,
    POSITION: 3,
    DECISIONS: 4,
    PERFORMANCE: 5,
    MARKET_DATA: 6,
    PNL: 7,
    // Rule Editor: a No-LLM agent's tab in the Prompt Editor's place, before Position.
    // Tabs are matched by value, so its number says nothing about where it sits.
    RULES: 8,
    // Data Simulator: last, for every agent. It loads its own data (AIAgentDataSimulator).
    SIMULATOR: 9,
};
// First tab that requires a selected agent.
const FIRST_AGENT_TAB = TAB.PROMPTS;

// Tabs that render the shared Variables / Position Sizing / Live Market Data
// sidebar. They all need `marketData` loaded and kept fresh.
const SIDEBAR_TABS = [TAB.PROMPTS, TAB.RULES, TAB.DECISIONS, TAB.PERFORMANCE];

// Tabs that re-poll `marketData` at the agent's Decision Timing. The Position tab
// renders no sidebar but shows the same position, which has to follow each fill —
// it used to load once on entry and then go stale.
const MARKET_DATA_POLL_TABS = [...SIDEBAR_TABS, TAB.POSITION];

// How often those tabs ask the server for the agent's position (a light request).
// The snapshot above reloads once per Decision Timing, so on a 5-minute agent the
// Variables panel trailed a fill by up to 5 minutes. When the position or the
// working orders differ from the snapshot on screen, the snapshot is reloaded.
const POSITION_POLL_SECONDS = 5;

// The blackboard's `working_orders` text as lines: this agent's orders the broker
// has not filled whole yet. 'NONE' is nothing working, and 'UNKNOWN (…)' means the
// read failed this cycle, not an order to show.
const workingOrderLines = (raw) => {
    const text = String(raw?.working_orders || '');
    return text && text !== 'NONE' && !text.startsWith('UNKNOWN')
        ? text.split('\n').filter(Boolean)
        : [];
};

// Sentinel row in the Decision Log's multi-select Action filter. Picking it
// clears the selection (empty array = no `action` param = every action); it is
// never sent to the server.
const ALL_ACTIONS = '__ALL__';

const PROMPT_VARIABLES = [
    { category: 'Snapshot', color: '#64748b', vars: [
        { name: 'timestamp', label: 'Timestamp' },
        { name: 'symbol', label: 'Symbol' },
    ]},
    { category: 'Broader Market', color: '#0ea5e9', vars: [
        { name: 'spy_daily_pct', label: 'SPY Daily Change %' },
        { name: 'avg_daily_volume', label: 'Avg Daily Volume (20d)' },
        { name: 'earnings_today', label: 'Earnings Today' },
        { name: 'fear_greed_index', label: 'Fear & Greed Index' },
    ]},
    { category: 'Technical Data', color: '#0ea5e9', vars: [
        { name: 'current_price', label: 'Current Price' },
        { name: 'vwap_upper', label: 'VWAP Upper' },
        { name: 'vwap_mid', label: 'VWAP Mid' },
        { name: 'vwap_lower', label: 'VWAP Lower' },
        { name: 'current_atr', label: 'ATR (14)' },
    ]},
    { category: 'Order Flow / L2', color: '#0ea5e9', vars: [
        { name: 'cmf', label: 'CMF (Chaikin Money Flow)' },
        { name: 'bbw', label: 'BBW (Bollinger Band Width)' },
        { name: 'bbw_14d_min', label: 'BBW 14d Min' },
        { name: 'bbw_14d_max', label: 'BBW 14d Max' },
        { name: 'bbw_is_tightest_14d', label: 'BBW Is 14d Tightest' },
        { name: 'l2_imbalance', label: 'L2 Imbalance (Ask/Bid)' },
    ]},
    { category: 'Options Data', color: '#8b5cf6', vars: [
        { name: 'put_wall', label: 'Put Wall', agent: 'options_volatility' },
        { name: 'call_wall', label: 'Call Wall', agent: 'options_volatility' },
        { name: 'gamma_flip', label: 'Gamma Flip', agent: 'options_volatility' },
        { name: 'max_pain', label: 'Max Pain', agent: 'options_volatility' },
        { name: 'dte', label: 'DTE (Nearest Expiry)', agent: 'options_volatility' },
    ]},
    { category: 'Macro Liquidity', color: '#059669', vars: [
        { name: 'fed_balance_sheet', label: 'Fed Balance Sheet', agent: 'macro_strategist' },
        { name: 'tga', label: 'TGA', agent: 'macro_strategist' },
        { name: 'reverse_repo', label: 'Reverse Repo', agent: 'macro_strategist' },
        { name: 'net_liquidity', label: 'Net Liquidity', agent: 'macro_strategist' },
        { name: 'wow_liquidity_change', label: 'WoW Liquidity Change', agent: 'macro_strategist' },
        { name: 'macro_bias', label: 'Macro Bias', agent: 'macro_strategist' },
        { name: 'prev_fed_balance_sheet', label: 'Prev Fed Balance Sheet', agent: 'macro_strategist' },
        { name: 'prev_tga', label: 'Prev TGA', agent: 'macro_strategist' },
        { name: 'prev_reverse_repo', label: 'Prev Reverse Repo', agent: 'macro_strategist' },
        { name: 'prev_net_liquidity', label: 'Prev Net Liquidity', agent: 'macro_strategist' },
        { name: 'market_trend_state', label: 'Market Trend State', agent: 'macro_strategist' },
    ]},
    { category: 'Crypto Arbitrage', color: '#f59e0b', vars: [
        { name: 'kimchi_premium', label: 'Kimchi Premium', agent: 'asset_strategist' },
        { name: 'cbp', label: 'Coinbase Premium (CBP)', agent: 'asset_strategist' },
        { name: 'krw_spread', label: 'KRW Spread', agent: 'asset_strategist' },
        { name: 'mnav', label: 'mNAV', agent: 'asset_strategist' },
        { name: 'btc_correlation', label: 'BTC Correlation (60m)', agent: 'asset_strategist' },
    ]},
    { category: 'Portfolio State', color: '#ec4899', vars: [
        { name: 'total_capital', label: 'Total Capital' },
        { name: 'cycle_trades', label: 'Cycle Trades' },
        { name: 'equity_avg_cost', label: 'Equity Avg Cost' },
        { name: 'strangle_status', label: 'Strangle Status', agent: 'options_volatility' },
        { name: 'open_positions', label: 'Open Positions' },
        // Sent to the broker but not filled whole yet. The fields above count only
        // what has filled, so these are what show a buy or sell still in flight.
        { name: 'working_orders', label: 'Working Orders (not filled)' },
        { name: 'working_buy_qty', label: 'Working BUY Qty' },
        { name: 'working_sell_qty', label: 'Working SELL Qty' },
    ]},
    { category: 'Strategic Flags', color: '#dc2626', vars: [
        { name: 'is_crypto_proxy', label: 'Is Crypto Proxy', agent: 'asset_strategist' },
        { name: 'has_mnav', label: 'Has mNAV', agent: 'asset_strategist' },
        { name: 'is_24_7_asset', label: 'Trades 24/7', agent: 'asset_strategist' },
        { name: 'gamma_regime', label: 'Gamma Regime', agent: 'options_volatility' },
        { name: 'out_of_bounds', label: 'Out of Bounds', agent: 'options_volatility' },
        { name: 'math_put', label: 'Math Put', agent: 'options_volatility' },
        { name: 'math_call', label: 'Math Call', agent: 'options_volatility' },
        { name: 'selected_put_strike', label: 'Selected Put Strike', agent: 'options_volatility' },
        { name: 'selected_call_strike', label: 'Selected Call Strike', agent: 'options_volatility' },
        { name: 'active_put_strike', label: 'Active Put Strike', agent: 'options_volatility' },
        { name: 'active_call_strike', label: 'Active Call Strike', agent: 'options_volatility' },
        { name: 'volarb_action', label: 'Vol-Arb Action', agent: 'options_volatility' },
        { name: 'system_halt', label: 'System Halt' },
        { name: 'market_state', label: 'Market State' },
        { name: 'authorized_direction', label: 'Authorized Direction' },
        { name: 'max_trades_allowed', label: 'Max Trades Allowed' },
        { name: 'ibit_rs_state', label: 'IBIT RS State', agent: 'asset_strategist' },
    ]},
    { category: 'Execution Gates', color: '#6366f1', vars: [
        { name: 'is_in_trading_hours', label: 'In Trading Hours' },
        { name: 'last_entry_price', label: 'Last Entry Price' },
        { name: 'candles_since_last_trade', label: 'Candles Since Last Trade' },
        { name: 'red_candles_since_last_trade', label: 'Red Candles Since Last Trade' },
        { name: 'green_candles_since_last_trade', label: 'Green Candles Since Last Trade' },
        { name: 'scaling_ready_long', label: 'Scaling Ready LONG' },
        { name: 'scaling_ready_short', label: 'Scaling Ready SHORT' },
        { name: 'last_closed_candle_color', label: 'Last Closed Candle Color' },
        // The last closed candle's close (the Fib preset's entry price) and the RED
        // run before it, pre-computed so the model doesn't count bars itself.
        { name: 'candle_close', label: 'Last Closed Candle Close' },
        { name: 'red_run_before_last_candle', label: 'Successive RED Before Last Candle' },
        { name: 'red_run_below_vwap_lower', label: 'Successive RED Below VWAP Lower' },
        { name: 'candle_body_ratio', label: 'Candle Body Ratio' },
        { name: 'candle_wick_long_ratio', label: 'Candle Wick Long Ratio' },
        { name: 'candle_wick_short_ratio', label: 'Candle Wick Short Ratio' },
    ]},
    { category: 'Position Sizing (1-2-4-8)', color: '#14b8a6', vars: [
        { name: 'sizing_unit', label: 'Unit Size (Capital / 15)' },
        { name: 'sizing_t1_dollars', label: 'Trade 1 $ (1x)' },
        { name: 'sizing_t1_shares', label: 'Trade 1 Shares (1x)' },
        { name: 'sizing_t2_dollars', label: 'Trade 2 $ (2x)' },
        { name: 'sizing_t2_shares', label: 'Trade 2 Shares (2x)' },
        { name: 'sizing_t3_dollars', label: 'Trade 3 $ (4x)' },
        { name: 'sizing_t3_shares', label: 'Trade 3 Shares (4x)' },
        { name: 'sizing_t4_dollars', label: 'Trade 4 $ (8x)' },
        { name: 'sizing_t4_shares', label: 'Trade 4 Shares (8x)' },
    ]},
    { category: 'History & Recent Candles', color: '#64748b', vars: [
        // Two separate blocks: `recent_candles` is the agent's own Timeframe
        // (5 Min etc.), `recent_candles_30s` is the 30-second tail. The Live
        // Market Data tab still shows them joined as one panel.
        { name: 'recent_candles', label: 'Recent Candles (agent Timeframe, last 1 hour)' },
        { name: 'recent_candles_30s', label: 'Recent Candles (30-Second, last 5 minutes)' },
        { name: 'realized_pnl', label: 'Realized P&L' },
        { name: 'unrealized_pnl', label: 'Unrealized P&L' },
        { name: 'total_pnl', label: 'Total P&L' },
        { name: 'win_rate', label: 'Win Rate' },
        { name: 'wins_losses', label: 'Wins / Losses' },
        { name: 'current_streak', label: 'Current Streak' },
        { name: 'recent_fills', label: 'Recent Fills (last 10)' },
        { name: 'recent_decisions', label: 'Recent Decisions (last 5)' },
    ]},
];

// Rule Editor: the values a rule's formulas may name come from the server
// (`variables` on GET .../rules), since it is what checks them. Only the colours of
// their groups live here.
const RULE_VARIABLE_GROUP_COLORS = {
    'Price & VWAP': '#0ea5e9',
    'Last Closed Candle': '#6366f1',
    Position: '#ec4899',
    'Position Sizing': '#14b8a6',
    'Options Data': '#8b5cf6',
    'Order Flow': '#0ea5e9',
    'Market & Flags': '#dc2626',
    'Built-in Signal': '#64748b',
};
const RULE_VARIABLE_TYPE_LABELS = {
    price: 'price (N/A at 0)',
    number: 'number',
    text: 'text',
    flag: 'true / false',
};

// True while a No-LLM agent decides through a rule and not the built-in router.
const runsOnRule = (agent) => agent?.config_json?.rule_engine?.active_rule != null;

// Render an API timestamp in an agent's configured `trading_timezone` rather
// than the viewer's browser timezone. The backend sends these offset-aware (see
// iso_with_server_offset in AIAgentModel.py), so `new Date` resolves the true
// instant and `timeZone` places it in the agent's trading day.
//
// Locale formatting is left to the browser — only the zone is pinned. Note what
// this replaces: an offset-less string parsed AND rendered as browser-local
// cancels out, so the old cell showed the server's wall clock verbatim to every
// viewer. Output only matches the old value where server tz == agent tz.
// An unknown/misspelled IANA name makes toLocaleString throw, so that falls back
// to viewer-local rather than blanking the cell.
const formatInAgentTz = (iso, tzName) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    if (!tzName) return d.toLocaleString();
    try {
        return d.toLocaleString(undefined, { timeZone: tzName });
    } catch (e) {
        return d.toLocaleString();
    }
};

// ==================== LIVE MARKET DATA — SHARED CATEGORY RENDERING ====================
// The sections, field rows and card grid live in ./MarketDataSections, which the simulators
// render their bar inspectors with too.


// ==================== STAT CARD ====================

const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ color, fontSize: 24 }} />
                </Box>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>{title}</Typography>
                    <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>{value}</Typography>
                    {subtitle && <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>{subtitle}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ==================== DECISION LOG CARDS ====================

// One card per decision action, above the Decision Log table. Buy / Sell / Hold
// always show; the other actions only once the agent has made such a decision, so
// a long-only agent is not shown six permanent zeros.
const ALWAYS_SHOWN_DECISION_ACTIONS = ['BUY', 'SELL', 'HOLD'];
const DECISION_CARD_STYLES = {
    BUY: { icon: MuiIcons.TrendingUp, color: '#10b981' },
    SELL: { icon: MuiIcons.TrendingDown, color: '#ef4444' },
    HOLD: { icon: MuiIcons.PauseCircleOutline, color: '#64748b' },
    SHORT: { icon: MuiIcons.ArrowDownward, color: '#f59e0b' },
    COVER: { icon: MuiIcons.Replay, color: '#0ea5e9' },
    CLOSE_ALL: { icon: MuiIcons.HighlightOff, color: '#ef4444' },
    EMERGENCY_EXIT: { icon: MuiIcons.ReportProblem, color: '#b91c1c' },
    STRANGLE_SELL: { icon: MuiIcons.CallSplit, color: '#8b5cf6' },
    STRANGLE_CLOSE: { icon: MuiIcons.CallMerge, color: '#0ea5e9' },
};
const DECISION_SOURCE_LABELS = { LLM: 'LLM only', PURE_CODE_ROUTER: 'Pure Code only', RULE_ENGINE: 'Rule only' };

// `summary` is the decisions endpoint's { total, executed, by_action,
// executed_by_action }. Driven off the shared enum, so a new action gets a card
// without a second list. Order: Total, Buy / Sell / Hold, the other actions, Executed.
const buildDecisionCards = (summary, sourceFilter) => {
    const byAction = summary?.by_action || {};
    const executedByAction = summary?.executed_by_action;
    const cards = [{
        key: 'TOTAL', title: 'Total Decisions', value: summary?.total || 0,
        icon: MuiIcons.Psychology, color: '#8b5cf6',
        // The cards follow the table's Source filter; say so while one is set.
        subtitle: DECISION_SOURCE_LABELS[sourceFilter],
    }];
    const actions = [
        ...ALWAYS_SHOWN_DECISION_ACTIONS,
        ...Object.values(AIAgentDecisionAction).filter((a) => !ALWAYS_SHOWN_DECISION_ACTIONS.includes(a)),
    ];
    actions.forEach((action) => {
        const count = byAction[action] || 0;
        if (!count && !ALWAYS_SHOWN_DECISION_ACTIONS.includes(action)) return;
        const style = DECISION_CARD_STYLES[action] || {};
        cards.push({
            key: action,
            title: `Total ${AIAgentDecisionActionLabels[action] || action}`,
            value: count,
            icon: style.icon || MuiIcons.Psychology,
            color: style.color || '#64748b',
            // A decision is not an order: say how many went out. A Hold never does.
            subtitle: action === 'HOLD' || !executedByAction
                ? undefined
                : `${(executedByAction[action] || 0).toLocaleString()} executed`,
        });
    });
    cards.push({
        key: 'EXECUTED', title: 'Executed', value: summary?.executed || 0,
        icon: MuiIcons.Bolt, color: '#06b6d4',
    });
    return cards;
};

// lightweight-charts accepts either "yyyy-mm-dd" or a UNIX second timestamp.
// Backend returns "yyyy-mm-dd HH:MM" for intraday bars, ALREADY shifted to the
// agent's timezone by `_ts_in_agent_tz` server-side. Parse as UTC (append 'Z')
// so lightweight-charts' UTC formatter renders the same wall-clock value.
const toChartTime = (t) => {
    if (t == null) return null;
    if (typeof t === 'number') return t;
    if (typeof t !== 'string') return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    let parsed = t.includes('T') ? t : t.replace(' ', 'T');
    if (!/Z$|[+-]\d{2}:?\d{2}$/.test(parsed)) parsed += 'Z';
    const ms = Date.parse(parsed);
    return isNaN(ms) ? null : Math.floor(ms / 1000);
};

// CandleChart MUST live at module scope. If it were defined inside AIAgentPage,
// every parent re-render (e.g. each SSE tick calling setMarketData) would
// produce a new forwardRef object — React would treat it as a different
// component type and unmount/remount the chart, throwing away every live bar
// that updateBar() just pushed in. That symptom is "Live Market Data tab is
// frozen even though SSE is connected and emitting ticks."
const CandleChart = forwardRef(({ data, height = 260, secondsVisible = false }, ref) => {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const candleSeriesRef = useRef(null);
    const volumeSeriesRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return undefined;

        const chart = createChart(containerRef.current, {
            width: containerRef.current.clientWidth,
            height,
            layout: {
                background: { type: ColorType.Solid, color: 'transparent' },
                textColor: '#94a3b8',
                fontSize: 11,
            },
            grid: {
                vertLines: { color: '#f1f5f9' },
                horzLines: { color: '#f1f5f9' },
            },
            rightPriceScale: { borderColor: '#e2e8f0' },
            timeScale: { borderColor: '#e2e8f0', timeVisible: true, secondsVisible },
            crosshair: { mode: 0 },
        });
        chartRef.current = chart;

        const candleSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#22c55e', downColor: '#ef4444',
            borderUpColor: '#22c55e', borderDownColor: '#ef4444',
            wickUpColor: '#22c55e', wickDownColor: '#ef4444',
            priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
        });
        candleSeriesRef.current = candleSeries;

        const volumeSeries = chart.addSeries(HistogramSeries, {
            priceFormat: { type: 'volume' },
            priceScaleId: 'volume',
        });
        volumeSeriesRef.current = volumeSeries;
        chart.priceScale('volume').applyOptions({
            scaleMargins: { top: 0.85, bottom: 0 },
        });

        // Follow the container, not only the window: its width also changes when a
        // scrollbar appears beside it (the Live Market Data drawer, the sidebar).
        const resizeObserver = new ResizeObserver(() => {
            const width = containerRef.current?.clientWidth;
            if (width) chart.applyOptions({ width });
        });
        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
            chart.remove();
            chartRef.current = null;
            candleSeriesRef.current = null;
            volumeSeriesRef.current = null;
        };
    }, [height, secondsVisible]);

    useEffect(() => {
        if (!candleSeriesRef.current || !volumeSeriesRef.current) return;
        if (!data || data.length === 0) {
            candleSeriesRef.current.setData([]);
            volumeSeriesRef.current.setData([]);
            return;
        }
        const candleData = data
            .filter(d => d.time && d.open != null && d.close != null)
            .map(d => ({ time: toChartTime(d.time), open: d.open, high: d.high, low: d.low, close: d.close }))
            .filter(d => d.time != null)
            .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
        const volumeData = data
            .filter(d => d.time && d.volume != null)
            .map(d => ({
                time: toChartTime(d.time), value: d.volume,
                color: (d.close || 0) >= (d.open || 0) ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)',
            }))
            .filter(d => d.time != null)
            .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0));
        candleSeriesRef.current.setData(candleData);
        volumeSeriesRef.current.setData(volumeData);
        chartRef.current?.timeScale().fitContent();
    }, [data]);

    useImperativeHandle(ref, () => ({
        updateBar(bar) {
            if (!bar || !candleSeriesRef.current) return;
            const t = typeof bar.time === 'number' ? bar.time : toChartTime(bar.time);
            if (t == null) return;
            try {
                candleSeriesRef.current.update({
                    time: t,
                    open: bar.open, high: bar.high, low: bar.low, close: bar.close,
                });
                if (volumeSeriesRef.current && bar.volume != null) {
                    volumeSeriesRef.current.update({
                        time: t,
                        value: bar.volume,
                        color: (bar.close ?? 0) >= (bar.open ?? 0)
                            ? 'rgba(34,197,94,0.3)'
                            : 'rgba(239,68,68,0.3)',
                    });
                }
            } catch (e) {
                // Lightweight-Charts throws if the new bar's time is older
                // than the most recent point. Swallow — usually means a
                // late-arriving tick after a bucket close, safe to drop.
            }
        },
    }), []);

    return <Box ref={containerRef} sx={{ width: '100%', minHeight: height }} />;
});

// ==================== MAIN COMPONENT ====================

const AIAgentPage = () => {
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState(null);
    // Agent whose .log is being built — that row's Logs icon shows a spinner.
    const [downloadingLogId, setDownloadingLogId] = useState(null);
    // Agent Management: the row whose Duplicate request is in flight.
    const [duplicatingId, setDuplicatingId] = useState(null);

    // Watchlist Symbols
    const [watchlistSymbols, setWatchlistSymbols] = useState([]);
    const [symbolsLoading, setSymbolsLoading] = useState(false);

    // Broker connections (for agent -> Alpaca linkage)
    const [brokerConnections, setBrokerConnections] = useState([]);
    const [brokerConnectionsLoading, setBrokerConnectionsLoading] = useState(false);

    // Timezones (for trading-window configuration)
    const [timezones, setTimezones] = useState([]);

    // Tab 0: Agent Management
    const [agents, setAgents] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(true);
    const [agentsPage, setAgentsPage] = useState(0);
    const [agentsPerPage, setAgentsPerPage] = useState(10);
    const [agentsTotal, setAgentsTotal] = useState(0);
    const [agentDialog, setAgentDialog] = useState(false);
    const [agentEditing, setAgentEditing] = useState(null);
    const [agentForm, setAgentForm] = useState({ ...INITIAL_AGENT_FORM });
    const [selectedAgent, setSelectedAgent] = useState(null);

    // Tab 1: Prompt Editor
    const [prompts, setPrompts] = useState([]);
    // Prompts from the owner's other agents (`shared` on GET .../prompts), listed read-only.
    const [sharedPrompts, setSharedPrompts] = useState([]);
    // The agent whose prompts the editor shows; responses for any other agent are dropped.
    const selectedAgentIdRef = useRef(null);
    const [promptsLoading, setPromptsLoading] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [promptEditorValue, setPromptEditorValue] = useState('');
    const [dataTemplateValue, setDataTemplateValue] = useState('');
    const [promptName, setPromptName] = useState('');
    const [promptNotes, setPromptNotes] = useState('');
    const [showDataTemplate, setShowDataTemplate] = useState(false);

    // Tab 1: Position Sizing (Martingale ladder editor — collapsible inside Prompt Editor).
    // Sourced from `selectedAgent.config_json.position_sizing` and persisted via
    // PUT /api/trade-execution/ai-agents/:id with `position_sizing: {...}`.
    const [showSizingEditor, setShowSizingEditor] = useState(false);
    const [showLiveMarket, setShowLiveMarket] = useState(false);
    const [sizingDivisor, setSizingDivisor] = useState(DEFAULT_POSITION_SIZING.divisor);
    const [sizingMultipliers, setSizingMultipliers] = useState([...DEFAULT_POSITION_SIZING.multipliers]);
    const [sizingSubmitting, setSizingSubmitting] = useState(false);
    const [sizingDirty, setSizingDirty] = useState(false);

    // Tab 1: VWAP Override (collapsible sidebar panel, same shape as Position
    // Sizing). Sourced from `selectedAgent.config_json.vwap_override` and
    // persisted via PUT /api/trade-execution/ai-agents/:id with
    // `vwap_override: {...}`. Bands are kept as strings so a blank field stays
    // blank — blank means "leave this band on live data", which is not 0.
    const [showVwapOverride, setShowVwapOverride] = useState(false);
    const [vwapManual, setVwapManual] = useState(false);
    const [vwapBands, setVwapBands] = useState({ upper: '', mid: '', lower: '' });
    const [vwapSubmitting, setVwapSubmitting] = useState(false);
    const [vwapDirty, setVwapDirty] = useState(false);

    // Tab 1: Prompt Builder (variables palette + copy-from-preset)
    const [focusedField, setFocusedField] = useState(null); // 'system_prompt' | 'data_template' | null
    const systemPromptRef = useRef(null);
    const dataTemplateRef = useRef(null);
    const [variableSearch, setVariableSearch] = useState('');
    const [showVariables, setShowVariables] = useState(true);
    const [presetTemplates, setPresetTemplates] = useState([]);
    const [presetsLoaded, setPresetsLoaded] = useState(false);
    const [presetMenuAnchor, setPresetMenuAnchor] = useState(null);

    // Rule Editor (No-LLM agents). The editor loads the variable catalog with its rules;
    // the shared sidebar lists it, and a clicked variable is inserted through the ref.
    const [ruleVariableGroups, setRuleVariableGroups] = useState([]);
    const ruleEditorRef = useRef(null);

    // Tab 2: Decision Log
    const [decisions, setDecisions] = useState([]);
    const [decisionsLoading, setDecisionsLoading] = useState(false);
    const [decisionsPage, setDecisionsPage] = useState(0);
    const [decisionsPerPage, setDecisionsPerPage] = useState(20);
    const [decisionsTotal, setDecisionsTotal] = useState(0);
    const [expandedDecision, setExpandedDecision] = useState(null);
    const [decisionsSourceFilter, setDecisionsSourceFilter] = useState('ALL');  // ALL | LLM | PURE_CODE_ROUTER | RULE_ENGINE
    // Multi-select: an array of AIAgentDecisionAction values. Empty = all actions.
    const [decisionsActionFilter, setDecisionsActionFilter] = useState([]);
    // Counts behind the cards above the table, kept with the agent they belong to
    // so another agent's totals are never shown while a load is in flight.
    const [decisionSummary, setDecisionSummary] = useState(null);
    // The latest loadDecisions call: only its response may land.
    const decisionsSeqRef = useRef(0);
    // The Decision Log's Live Market Data drawer.
    const [liveDrawerOpen, setLiveDrawerOpen] = useState(false);

    // "View Snapshot" dialog: the market data the agent actually ran on.
    const [snapshotCycle, setSnapshotCycle] = useState(null);       // { cycleId, decision } | null
    const [snapshotByCycle, setSnapshotByCycle] = useState({});     // cycleId -> payload (immutable, never invalidated)
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [snapshotChartReady, setSnapshotChartReady] = useState(false);

    // Tab 3: Performance
    const [performance, setPerformance] = useState(null);
    const [performanceLoading, setPerformanceLoading] = useState(false);
    const [cycles, setCycles] = useState([]);
    const [cyclesLoading, setCyclesLoading] = useState(false);

    // Tab 4: Live Market Data
    const [marketData, setMarketData] = useState(null);
    const [marketDataLoading, setMarketDataLoading] = useState(false);
    const [streamLive, setStreamLive] = useState(false);
    // Imperative ref into the 30-second CandleChart on the Live Market Data tab.
    // The SSE handler below calls .updateBar() on it without triggering a React
    // re-render, which is what makes that chart move smoothly per-tick
    // (TradingView style) instead of flickering on each setData rebuild. The
    // Timeframe chart above it takes no live bar: Recent Candles shows closed
    // candles only, and reloads when one closes (see the effect after the SSE one).
    const chartBottomRef = useRef(null);  // 30s-tail chart (Last 5 Minutes)
    // In-progress 30s bar state, keyed off the current bucket time. Reset to null
    // on agent change / SSE reopen so a stale bucket from a previous symbol can't
    // leak into the new chart.
    const bottomInProgressRef = useRef(null);
    const [fetchingCrypto, setFetchingCrypto] = useState(false);
    const [fetchingL2, setFetchingL2] = useState(false);
    const [fetchingMacro, setFetchingMacro] = useState(false);

    // Tab 5: P&L Report
    const [pnlData, setPnlData] = useState(null);
    const [pnlLoading, setPnlLoading] = useState(false);
    const [pnlPage, setPnlPage] = useState(0);
    const [pnlPerPage, setPnlPerPage] = useState(20);
    const [pnlTotal, setPnlTotal] = useState(0);

    // ==================== PERMISSIONS ====================

    useEffect(() => {
        const checkPerms = async () => {
            setCanView(await hasPermission('Trade Execution Engine', 'can_view'));
            setCanAdd(await hasPermission('Trade Execution Engine', 'can_add'));
            setCanEdit(await hasPermission('Trade Execution Engine', 'can_edit'));
            setCanDelete(await hasPermission('Trade Execution Engine', 'can_delete'));
        };
        checkPerms();
    }, []);

    // ==================== DATA LOADING ====================

    const fetchWatchlistSymbols = useCallback(async () => {
        setSymbolsLoading(true);
        try {
            const params = new URLSearchParams({ page: 1, per_page: 500, status: 'ACTIVE' });
            const response = await axiosInstance.get(`/api/stock-trading/symbols?${params.toString()}`);
            if (response.data.success) {
                setWatchlistSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch watchlist symbols:', err);
        } finally {
            setSymbolsLoading(false);
        }
    }, []);

    const fetchBrokerConnections = useCallback(async () => {
        setBrokerConnectionsLoading(true);
        try {
            const res = await axiosInstance.get('/api/trade-execution/broker-connections');
            setBrokerConnections(res.data?.data || res.data || []);
        } catch (err) {
            console.error('Failed to fetch broker connections:', err);
        } finally {
            setBrokerConnectionsLoading(false);
        }
    }, []);

    const fetchTimezones = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/selections/timezones');
            const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            setTimezones(list);
        } catch (err) {
            console.error('Failed to fetch timezones:', err);
        }
    }, []);

    const loadAgents = useCallback(async () => {
        setAgentsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents?page=${agentsPage + 1}&limit=${agentsPerPage}`);
            setAgents(res.data.data || []);
            setAgentsTotal(res.data.total || 0);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load agents');
        } finally {
            setAgentsLoading(false);
        }
    }, [agentsPage, agentsPerPage]);

    const loadPrompts = useCallback(async (agentId) => {
        if (!agentId) return;
        setPromptsLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/prompts`);
            // A slow response for an agent no longer selected must not fill this one's lists.
            if (selectedAgentIdRef.current !== agentId) return;
            setPrompts(res.data.data || []);
            // Prompts from the owner's other agents; nothing is copied until one is used.
            setSharedPrompts(res.data.shared || []);
        } catch (err) {
            if (selectedAgentIdRef.current !== agentId) return;
            // Never leave the previous load's shared list on screen.
            setSharedPrompts([]);
            setError(err.response?.data?.error || 'Failed to load prompts');
        } finally {
            if (selectedAgentIdRef.current === agentId) setPromptsLoading(false);
        }
    }, []);

    const loadDecisions = useCallback(async (agentId) => {
        if (!agentId) return;
        // A slow response for an earlier page, filter or agent must not overwrite a
        // newer one, or the cards and the rows would come from different loads.
        const seq = ++decisionsSeqRef.current;
        setDecisionsLoading(true);
        try {
            const sourceParam = decisionsSourceFilter && decisionsSourceFilter !== 'ALL'
                ? `&source=${decisionsSourceFilter}` : '';
            // Filtering is server-side because the table is server-paginated -
            // filtering the current page only would report the wrong total and
            // silently hide matches on other pages.
            // Comma-separated so several actions can be filtered at once; the
            // backend turns the list into an IN clause.
            const actionParam = decisionsActionFilter.length
                ? `&action=${encodeURIComponent(decisionsActionFilter.join(','))}` : '';
            const res = await axiosInstance.get(
                `/api/trade-execution/ai-agents/${agentId}/decisions?page=${decisionsPage + 1}&limit=${decisionsPerPage}${sourceParam}${actionParam}`
            );
            if (seq !== decisionsSeqRef.current) return;
            setDecisions(res.data.data || []);
            setDecisionsTotal(res.data.total || 0);
            setDecisionSummary(res.data.summary ? { agentId, ...res.data.summary } : null);
        } catch (err) {
            if (seq !== decisionsSeqRef.current) return;
            setError(err.response?.data?.error || 'Failed to load decisions');
        } finally {
            if (seq === decisionsSeqRef.current) setDecisionsLoading(false);
        }
    }, [decisionsPage, decisionsPerPage, decisionsSourceFilter, decisionsActionFilter]);

    // The snapshot belongs to the cycle, not the decision — several decisions
    // from one cycle share it, so the cache is keyed on cycleId and opening the
    // second decision of a cycle costs no request.
    const openDecisionSnapshot = useCallback(async (agentId, decision) => {
        const cycleId = decision?.cycle_id;
        if (!agentId || !cycleId) return;

        setSnapshotCycle({ cycleId, decision });
        setSnapshotChartReady(false);
        if (snapshotByCycle[cycleId]) return;

        setSnapshotLoading(true);
        try {
            const res = await axiosInstance.get(
                `/api/trade-execution/ai-agents/${agentId}/cycles/${cycleId}/market-data`
            );
            setSnapshotByCycle((prev) => ({
                ...prev,
                // `data` is null when the cycle stored no snapshot. The reason
                // and the cycle identity ride at the top level in that case, so
                // the dialog can still say which cycle it is and why it's empty.
                [cycleId]: {
                    data: res.data.data,
                    reason: res.data.reason,
                    snapshot_at: res.data.snapshot_at,
                    cycle_number: res.data.cycle_number,
                },
            }));
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load market data snapshot');
            setSnapshotCycle(null);
        } finally {
            setSnapshotLoading(false);
        }
    }, [snapshotByCycle]);

    const closeDecisionSnapshot = useCallback(() => {
        setSnapshotCycle(null);
        setSnapshotChartReady(false);
    }, []);

    const loadPerformance = useCallback(async (agentId) => {
        if (!agentId) return;
        setPerformanceLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/performance`);
            setPerformance(res.data.data || null);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load performance');
        } finally {
            setPerformanceLoading(false);
        }
    }, []);

    const loadCycles = useCallback(async (agentId) => {
        if (!agentId) return;
        setCyclesLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/cycles?page=1&limit=10`);
            setCycles(res.data.data || []);
        } catch (err) {
            console.error('Failed to load cycles');
        } finally {
            setCyclesLoading(false);
        }
    }, []);

    // The selected agent, for the snapshot loaders. Only the effect below writes it. A slow
    // answer for the previously selected agent must not land under the current one, and a
    // handler that was waiting on something else when the agent changed (Fetch L2, saving
    // the VWAP override) must not load the agent it was started for.
    const marketDataAgentRef = useRef(null);
    // Snapshot requests are numbered in the order they are sent. The Decision-Timing poll
    // and a reload the position poll asked for can be in flight together, and an older
    // answer landing last would put a position back that has already changed.
    const marketDataSeqRef = useRef(0);
    // The snapshot on screen: its agent, its request's number, and its position version
    // for the position poll.
    const marketDataShownRef = useRef({ agentId: null, seq: 0, positionVersion: null });

    // Whether request `seq` for `agentId` may still land: the agent is still the one
    // selected, and no later request's answer is already on screen. An answer is never
    // dropped only because a later request has been sent: when the snapshot takes longer
    // than the poll, that would leave the panel without any answer at all.
    const marketDataMayLand = useCallback((agentId, seq) => (
        marketDataAgentRef.current === agentId && seq > marketDataShownRef.current.seq
    ), []);

    const showMarketData = useCallback((agentId, seq, data) => {
        marketDataShownRef.current = { agentId, seq, positionVersion: data?.position_version || null };
        setMarketData(data || null);
    }, []);

    // `silent` (the Decision-Timing poll) skips the loading state, so a tick doesn't
    // blink the spinners or disable the refresh buttons. Errors still surface.
    const loadMarketData = useCallback(async (agentId, { silent = false } = {}) => {
        if (!agentId || marketDataAgentRef.current !== agentId) return;
        const seq = ++marketDataSeqRef.current;
        if (!silent) setMarketDataLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/market-data`);
            if (!marketDataMayLand(agentId, seq)) return;
            showMarketData(agentId, seq, res.data.data);
        } catch (err) {
            if (!marketDataMayLand(agentId, seq)) return;
            setError(err.response?.data?.error || 'Failed to load market data');
        } finally {
            if (!silent) setMarketDataLoading(false);
        }
    }, [marketDataMayLand, showMarketData]);

    // A refresh syncs bars on the server first, so a poll sent after it can answer before it.
    // That poll's snapshot is the later read of the position and stays on screen; the refresh
    // still succeeded, and says so. Its bars show with the next load.
    const refreshMarketData = useCallback(async (agentId, silent = false) => {
        if (!agentId || marketDataAgentRef.current !== agentId) return;
        const seq = ++marketDataSeqRef.current;
        if (!silent) setMarketDataLoading(true);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${agentId}/market-data/refresh`);
            if (marketDataAgentRef.current !== agentId) return;
            if (marketDataMayLand(agentId, seq)) showMarketData(agentId, seq, res.data.data);
            if (!silent) setSnackbar({ message: 'Market data refreshed', severity: 'success' });
        } catch (err) {
            if (!marketDataMayLand(agentId, seq)) return;
            if (!silent) setError(err.response?.data?.error || 'Failed to refresh market data');
        } finally {
            if (!silent) setMarketDataLoading(false);
        }
    }, [marketDataMayLand, showMarketData]);

    // Another agent's snapshot is never shown under the selected one: the panels are empty
    // until this agent's own snapshot lands, and a late answer for the previous agent is
    // dropped even on a tab that loads no snapshot. Declared before the effects that load:
    // the loaders refuse an agent other than the one set here.
    useEffect(() => {
        marketDataAgentRef.current = selectedAgent?.id ?? null;
        marketDataShownRef.current = { ...marketDataShownRef.current, agentId: null, positionVersion: null };
        setMarketData(null);
    }, [selectedAgent?.id]);

    const loadPnlReport = useCallback(async (agentId, page = 1, perPage = 20) => {
        if (!agentId) return;
        setPnlLoading(true);
        try {
            const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/pnl-report?page=${page}&limit=${perPage}`);
            setPnlData(res.data.data || null);
            setPnlTotal(res.data.data?.pagination?.total || 0);
        } catch (err) {
            setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load P&L report');
        } finally {
            setPnlLoading(false);
        }
    }, []);

    useEffect(() => { loadAgents(); fetchWatchlistSymbols(); fetchBrokerConnections(); fetchTimezones(); }, [loadAgents, fetchWatchlistSymbols, fetchBrokerConnections, fetchTimezones]);

    useEffect(() => {
        if (selectedAgent) {
            if (activeTab === TAB.PROMPTS) { loadPrompts(selectedAgent.id); loadMarketData(selectedAgent.id); }
            // The Rule Editor loads its own rules; the sidebar beside it needs the market data.
            if (activeTab === TAB.RULES) loadMarketData(selectedAgent.id);
            // Position reads the same derived position state the blackboard carries.
            if (activeTab === TAB.POSITION) loadMarketData(selectedAgent.id);
            // Decision Log + Performance render the shared sidebar too, so they
            // need the same market-data payload the Prompt Editor loads.
            if (activeTab === TAB.DECISIONS) { loadDecisions(selectedAgent.id); loadMarketData(selectedAgent.id); }
            if (activeTab === TAB.PERFORMANCE) { loadPerformance(selectedAgent.id); loadCycles(selectedAgent.id); loadMarketData(selectedAgent.id); }
            if (activeTab === TAB.MARKET_DATA) refreshMarketData(selectedAgent.id);
            if (activeTab === TAB.PNL) loadPnlReport(selectedAgent.id, pnlPage + 1, pnlPerPage);
        }
    }, [selectedAgent, activeTab, loadPrompts, loadDecisions, loadPerformance, loadCycles, refreshMarketData, loadMarketData, loadPnlReport, pnlPage, pnlPerPage]);

    // Refresh `marketData` at the agent's Decision Timing cadence on every tab in
    // MARKET_DATA_POLL_TABS: the sidebar's share counts stay aligned with the price
    // the next cycle will see, and the Position tab follows each fill. Silent, so the
    // loading state (and the Position refresh button) doesn't blink on every tick.
    useEffect(() => {
        if (!MARKET_DATA_POLL_TABS.includes(activeTab) || !selectedAgent?.id) return undefined;
        const intervalSec = Math.max(5, selectedAgent?.cycle_interval_seconds || 30);
        const id = setInterval(() => {
            loadMarketData(selectedAgent.id, { silent: true });
        }, intervalSec * 1000);
        return () => clearInterval(id);
    }, [activeTab, selectedAgent?.id, selectedAgent?.cycle_interval_seconds, loadMarketData]);

    // The position follows fills, not Decision Timing. Every POSITION_POLL_SECONDS the
    // same tabs ask for the agent's position `version`; when it differs from the one of
    // the snapshot on screen, an order was sent, filled (in part too), cancelled or
    // expired since, and the snapshot is reloaded so every panel shows it.
    useEffect(() => {
        if (!MARKET_DATA_POLL_TABS.includes(activeTab) || !selectedAgent?.id) return undefined;
        const agentId = selectedAgent.id;
        let stopped = false;
        let inFlight = false;
        // One reload per position: if that reload fails, the next Decision-Timing poll
        // catches up, and the page never hammers the snapshot endpoint.
        let reloadedFor = null;
        const tick = async () => {
            if (inFlight || document.hidden) return;
            inFlight = true;
            try {
                // With a timeout: a request that never answers would leave `inFlight` set
                // and end the poll.
                const res = await axiosInstance.get(`/api/trade-execution/ai-agents/${agentId}/position`,
                    { timeout: POSITION_POLL_SECONDS * 2000 });
                const version = res.data?.data?.version;
                if (stopped || !version) return;
                // In step: this agent's snapshot is on screen and carries this version. With no
                // snapshot of this agent on screen (its load is slow, or failed) it is asked
                // for as well, so the panels do not stay empty until the next Decision Timing.
                const shown = marketDataShownRef.current;
                const inStep = shown.agentId === agentId && shown.positionVersion === version;
                if (!inStep && version !== reloadedFor) {
                    reloadedFor = version;
                    loadMarketData(agentId, { silent: true });
                }
            } catch (err) {
                // Silent: the next tick asks again, and the Decision-Timing poll still runs.
            } finally {
                inFlight = false;
            }
        };
        const id = setInterval(tick, POSITION_POLL_SECONDS * 1000);
        return () => { stopped = true; clearInterval(id); };
    }, [activeTab, selectedAgent?.id, loadMarketData]);

    // Hydrate the position-sizing editor whenever the selected agent changes.
    // Falls back to the platform default when the agent has no override stored.
    useEffect(() => {
        const ps = selectedAgent?.config_json?.position_sizing;
        if (ps && typeof ps === 'object' && Array.isArray(ps.multipliers) && ps.multipliers.length === 4) {
            setSizingDivisor(Number(ps.divisor) || DEFAULT_POSITION_SIZING.divisor);
            setSizingMultipliers(ps.multipliers.map(m => Number(m) || 0));
        } else {
            setSizingDivisor(DEFAULT_POSITION_SIZING.divisor);
            setSizingMultipliers([...DEFAULT_POSITION_SIZING.multipliers]);
        }
        setSizingDirty(false);
    }, [selectedAgent?.id, selectedAgent?.config_json]);

    // Hydrate the VWAP override editor from the selected agent. An absent block
    // means live data — today's behaviour — and any saved band values are kept
    // even while disabled, so flipping back to Manual doesn't mean retyping them.
    useEffect(() => {
        const vo = selectedAgent?.config_json?.vwap_override;
        setVwapManual(Boolean(vo?.enabled));
        setVwapBands({
            upper: vo?.upper != null ? String(vo.upper) : '',
            mid: vo?.mid != null ? String(vo.mid) : '',
            lower: vo?.lower != null ? String(vo.lower) : '',
        });
        setVwapDirty(false);
    }, [selectedAgent?.id, selectedAgent?.config_json]);

    // Live streaming via SSE — replaces the previous setInterval polling.
    //
    // Flow on tab open:
    //   1. The Live Market Data tab effect above already kicked a one-shot
    //      refreshMarketData() call to populate the initial state + chart bars.
    //   2. This effect opens an SSE connection to the backend
    //      (/api/trade-execution/ai-agents/:id/market-data/stream) which
    //      forwards live trades from Alpaca's WebSocket SIP feed. Every trade
    //      arrives as a `tick` event; closed 30s buckets arrive as `bar_30s`.
    //   3. On each tick: setMarketData() updates the price + VWAP bands
    //      (cheap React reconciliation, no chart remount), AND we compute the
    //      30s bucket time and push the in-progress candle into the 30s chart
    //      via the imperative .updateBar() method. The TradingView chart
    //      merges by time — same time = update last candle in place; new time
    //      = append. The Timeframe chart takes no live candle: Recent Candles
    //      shows closed candles only.
    //
    // Timezone alignment: backend emits UTC timestamps in `ts`. The initial
    // REST-loaded bars are TZ-shifted server-side to the agent's TZ
    // (recent_candles_data[].time = "yyyy-mm-dd HH:MM" in agent TZ). To keep
    // the live in-progress bar visually aligned with the existing bars on the
    // chart, we apply the same shift here in the browser using the
    // marketData.agent_timezone string.
    const utcSecondsToAgentTzShifted = useCallback((utcMillis, tzName) => {
        if (!tzName) return Math.floor(utcMillis / 1000);
        try {
            const fmt = new Intl.DateTimeFormat('en-US', {
                timeZone: tzName,
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                hour12: false,
            });
            const parts = fmt.formatToParts(new Date(utcMillis));
            const get = (type) => parts.find(p => p.type === type)?.value || '00';
            const y = get('year'); const m = get('month'); const day = get('day');
            const h = get('hour') === '24' ? '00' : get('hour');
            const mn = get('minute'); const s = get('second');
            const ms = Date.parse(`${y}-${m}-${day}T${h}:${mn}:${s}Z`);
            return Number.isNaN(ms) ? Math.floor(utcMillis / 1000) : Math.floor(ms / 1000);
        } catch (e) {
            return Math.floor(utcMillis / 1000);
        }
    }, []);

    // Hold tzName in a ref so the SSE handler closure always reads the latest
    // value without forcing the useEffect below to tear down the stream every
    // time agent_timezone changes (which happens when the REST snapshot lands
    // a few hundred ms after the SSE connects).
    const tzNameRef = useRef(null);
    useEffect(() => {
        tzNameRef.current = marketData?.agent_timezone || selectedAgent?.trading_timezone || null;
    }, [marketData?.agent_timezone, selectedAgent?.trading_timezone]);

    // Where the live views are on screen. The stream runs for the Live Market Data
    // view: its own tab, or the Decision Log's drawer, which renders that same view.
    const liveDrawerShown = liveDrawerOpen && activeTab === TAB.DECISIONS;
    const liveMarketViewOpen = activeTab === TAB.MARKET_DATA || liveDrawerShown;
    // Recent Candles is also in the sidebar's Live Market Data panel.
    const recentCandlesShown = liveMarketViewOpen
        || (SIDEBAR_TABS.includes(activeTab) && showLiveMarket);

    useEffect(() => {
        if (!liveMarketViewOpen || !selectedAgent?.id) return undefined;

        // Reset the in-progress 30s bar so a previous agent's bucket can't leak
        // into the new agent's chart on tab/agent switch.
        bottomInProgressRef.current = null;
        setStreamLive(false);

        // Streaming-disabled fallback: when ALPACA_STREAM_ENABLED=false on the
        // backend the Alpaca WebSocket pipeline is off, so no `tick` events
        // will ever flow through SSE. Instead, poll the lightweight
        // /market-data endpoint at the agent's Decision Timing cadence —
        // that's the user-defined refresh rate for the stream-derived fields
        // (current_price, VWAP bands, intra-30s bars). Each poll replaces
        // marketData wholesale so the chart's [data] effect fires and the
        // displayed price/VWAP/candles refresh together.
        //
        // Why `=== false` rather than truthy-check: on the very first render
        // marketData is still null (REST hasn't landed yet) and we want to
        // open SSE optimistically so streaming-on users get instant ticks.
        // Once the REST response lands with the actual flag, this effect
        // re-runs and either keeps the SSE (true / undefined) or tears it
        // down and starts polling (false).
        if (marketData?.streaming_enabled === false) {
            // The Decision Log already polls at this cadence (MARKET_DATA_POLL_TABS),
            // so its drawer needs no second timer.
            if (activeTab !== TAB.MARKET_DATA) return undefined;
            const intervalSec = Math.max(
                5,
                selectedAgent?.cycle_interval_seconds || 300,
            );
            const id = setInterval(() => {
                loadMarketData(selectedAgent.id);
            }, intervalSec * 1000);
            return () => clearInterval(id);
        }

        const updateInProgressBar = (stateRef, chartRef, tickSec, bucketSec, price) => {
            const bucketStart = Math.floor(tickSec / bucketSec) * bucketSec;
            let bar = stateRef.current;
            if (!bar || bar.time !== bucketStart) {
                bar = { time: bucketStart, open: price, high: price, low: price, close: price, volume: 0 };
            } else {
                if (price > bar.high) bar.high = price;
                if (price < bar.low) bar.low = price;
                bar.close = price;
            }
            stateRef.current = bar;
            chartRef.current?.updateBar(bar);
        };

        const stream = openLiveMarketDataStream(selectedAgent.id, {
            onSnapshot: (snap) => {
                setStreamLive(true);
                // Seed marketData if REST hasn't returned yet so the first
                // visible price comes from the snapshot — otherwise the
                // initial frame is silently dropped.
                // Price only. The stream's vwap_* is a SESSION VWAP (every
                // trade since UTC midnight, no timeframe dimension), so writing
                // it here made the bands identical for every Timeframe. The
                // timeframe-scoped bands arrive on the REST payload under
                // `raw.vwap_*` / `categories.technical_data`.
                setMarketData(prev => ({
                    ...(prev || {}),
                    current_price: snap.price || prev?.current_price,
                }));
            },
            onTick: (evt) => {
                setStreamLive(true);
                // Price ticks live; VWAP does not - see onSnapshot above. A tick at
                // the same price returns the same object, so React skips the
                // re-render: under the drawer that render is the whole Decision Log.
                setMarketData(prev => (prev?.current_price === evt.price ? prev : {
                    ...(prev || {}),
                    current_price: evt.price,
                }));

                if (!evt.ts || evt.price == null) return;
                const tickMs = Date.parse(evt.ts);
                if (Number.isNaN(tickMs)) return;
                const tickSec = utcSecondsToAgentTzShifted(tickMs, tzNameRef.current);

                // Only the 30s chart takes a live bar: the Timeframe chart shows
                // closed candles only.
                updateInProgressBar(bottomInProgressRef, chartBottomRef, tickSec, 30, evt.price);
            },
            onBar30s: (bar) => {
                if (!bar?.time) return;
                // bar.time from backend is naive UTC isoformat (the bucket
                // start). Convert to agent-TZ-shifted unix seconds the same
                // way ticks are.
                const ms = Date.parse(bar.time.endsWith('Z') ? bar.time : bar.time + 'Z');
                if (Number.isNaN(ms)) return;
                const t = utcSecondsToAgentTzShifted(ms, tzNameRef.current);
                chartBottomRef.current?.updateBar({
                    time: t,
                    open: bar.open, high: bar.high, low: bar.low, close: bar.close,
                    volume: bar.volume,
                });
                // Force the next tick to start a fresh in-progress 30s bucket
                // off this confirmed close.
                bottomInProgressRef.current = null;
            },
            onError: (err) => {
                // Network blip / token expired / server restart — the polyfill
                // auto-reconnects, so we just flip the indicator to "stale"
                // until the next event lands.
                console.warn('Live Market Data stream error:', err);
                setStreamLive(false);
            },
        });

        return () => {
            stream.close();
            setStreamLive(false);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeTab,
        liveMarketViewOpen,
        selectedAgent?.id,
        selectedAgent?.cycle_interval_seconds,
        marketData?.streaming_enabled,
        loadMarketData,
        utcSecondsToAgentTzShifted,
    ]);

    // Recent Candles lists closed candles only, so it changes only when a candle
    // closes: reload right then instead of on the next Decision Timing poll.
    // agent_timeframe_bar_sync (HistoricalDataScheduler) stores the closed bar about
    // 2 s after the close, so the reload comes 2.5 s after it; while the bar is still
    // missing it retries every 2 s for up to a minute. Further behind than that
    // (market closed, sync stopped) it waits for the next close. Keyed on the
    // `recent_candles_data` array, which only a REST load replaces, so SSE ticks
    // don't restart the timer. Runs wherever Recent Candles is on screen: the Live
    // Market Data tab, the Decision Log's drawer, and the sidebar's panel.
    useEffect(() => {
        if (!recentCandlesShown || !selectedAgent?.id) return undefined;
        const closed = marketData?.recent_candles_data;
        const last = closed?.[closed.length - 1];
        // The Timeframe actually shown, fallback included: the text's header line.
        const timeframe = /^Timeframe: (\w+)/.exec(marketData?.recent_candles || '')?.[1];
        const barSec = timeframe ? timeframeToSeconds(timeframe) : 0;
        const lastStart = last ? toChartTime(last.time) : null;
        if (typeof lastStart !== 'number' || !barSec || barSec >= 86400) return undefined;
        // Both on the agent-timezone clock the candle labels use.
        const nowSec = utcSecondsToAgentTzShifted(Date.now(), marketData?.agent_timezone);
        // Seconds since the bar after the newest one closed; negative while it is open.
        const late = nowSec - (lastStart + 2 * barSec);
        let waitSec = 2;
        if (late < 0) waitSec = 2.5 - late;
        else if (late >= 60) waitSec = barSec - (late % barSec) + 2.5;
        // A browser clock days off must not push the delay past setTimeout's limit,
        // where it fires at once and each reload would schedule the next.
        waitSec = Math.min(Math.max(waitSec, 2), barSec + 5);
        const id = setTimeout(() => loadMarketData(selectedAgent.id, { silent: true }), waitSec * 1000);
        return () => clearTimeout(id);
    }, [
        recentCandlesShown,
        selectedAgent?.id,
        marketData?.recent_candles_data,
        marketData?.recent_candles,
        marketData?.agent_timezone,
        loadMarketData,
        utcSecondsToAgentTzShifted,
    ]);

    useEffect(() => {
        if (!selectedAgent && activeTab >= FIRST_AGENT_TAB) setActiveTab(TAB.AGENTS);
        // A NO_LLM agent has a Rule Editor where an LLM agent has a Prompt Editor. Switching
        // agents while on one of them lands on the other agent's own editor.
        if (selectedAgent && selectedAgent.execution_mode === 'NO_LLM' && activeTab === TAB.PROMPTS) {
            setActiveTab(TAB.RULES);
        }
        if (selectedAgent && selectedAgent.execution_mode !== 'NO_LLM' && activeTab === TAB.RULES) {
            setActiveTab(TAB.PROMPTS);
        }
    }, [selectedAgent, activeTab]);

    // A prompt selection belongs to one agent's list: a version's id, or a shared entry,
    // means nothing in another agent's. Keyed on the id, so tab switches and saving an
    // edit to the same agent keep it.
    useEffect(() => {
        selectedAgentIdRef.current = selectedAgent?.id ?? null;
        setSelectedPrompt(null);
    }, [selectedAgent?.id]);

    // The Live Market Data drawer belongs to the Decision Log: leaving the tab, or
    // the agent, closes it, so it does not reopen on the way back.
    useEffect(() => {
        setLiveDrawerOpen(false);
    }, [activeTab, selectedAgent?.id]);

    // ==================== AGENT CRUD HANDLERS ====================

    const handleCreateAgent = async () => {
        setSubmitting(true);
        try {
            const { enabled_agents, order_types, order_cancellation, ...rest } = agentForm;
            const payload = {
                ...rest,
                enabled_agents,
                // Top level too, so the update controller's merge branch fires
                // and validates it — same pattern as enabled_agents.
                order_types,
                order_cancellation,
                config_json: {
                    ...(agentEditing?.config_json || {}),
                    enabled_agents,
                    order_types,
                    order_cancellation,
                    timeframe: agentForm.timeframe,
                },
                // Sent at the top level so the controller's validation branch
                // fires; '' clears it back to "same as Timeframe".
                vwap_timeframe: agentForm.vwap_timeframe || '',
            };
            // Optional money fields — send a number or null, never ''. The old
            // `if (payload.x)` guard was falsy for '' and let the blank string
            // through, which MySQL rejects for a FLOAT column (error 1366).
            ['capital_allocation', 'max_position_value', 'max_daily_loss'].forEach((field) => {
                const parsed = parseFloat(payload[field]);
                payload[field] = Number.isFinite(parsed) ? parsed : null;
            });
            payload.broker_connection_id = payload.broker_connection_id || null;
            // A blank date means no bound; null (never '') so an update clears a stored one.
            payload.trading_start_date = payload.trading_start_date || null;
            payload.trading_end_date = payload.trading_end_date || null;
            // Decision Timing — coerce blanks/non-numerics back to the 30s default
            // so the backend never sees `''` for an Integer column.
            const _cis = parseInt(payload.cycle_interval_seconds, 10);
            payload.cycle_interval_seconds = Number.isFinite(_cis) && _cis > 0 ? _cis : 30;

            if (agentEditing) {
                // execution_mode is locked at creation time — never send it on update.
                delete payload.execution_mode;
                const res = await axiosInstance.put(`/api/trade-execution/ai-agents/${agentEditing.id}`, payload);
                // Keep `selectedAgent` in sync with the saved record so the Live
                // Market Data auto-refresh useEffect picks up the new
                // `cycle_interval_seconds` (Decision Timing) immediately —
                // without this, `loadAgents()` refreshes the list but the
                // currently-selected reference stays stale and the refresh
                // interval keeps the old cadence.
                const updated = res?.data?.data;
                if (updated && selectedAgent?.id === agentEditing.id) {
                    setSelectedAgent(updated);
                }
                setSnackbar({ message: 'Agent updated', severity: 'success' });
            } else {
                await axiosInstance.post('/api/trade-execution/ai-agents', payload);
                setSnackbar({ message: 'Agent created with default prompt', severity: 'success' });
            }
            setAgentDialog(false);
            setAgentEditing(null);
            setAgentForm({ ...INITIAL_AGENT_FORM });
            loadAgents();
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to save agent', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteAgent = async (id) => {
        if (!window.confirm('Are you sure you want to delete this agent?')) return;
        try {
            await axiosInstance.delete(`/api/trade-execution/ai-agents/${id}`);
            setSnackbar({ message: 'Agent deleted', severity: 'success' });
            loadAgents();
        } catch (err) {
            setSnackbar({ message: 'Failed to delete agent', severity: 'error' });
        }
    };

    // The backend copies the configuration and every prompt version, keeps the same
    // one active and leaves the copy STOPPED, so this is a single request.
    const handleDuplicateAgent = async (agent) => {
        setDuplicatingId(agent.id);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${agent.id}/duplicate`);
            setSnackbar({ message: res.data?.message || 'Agent duplicated', severity: 'success' });
            loadAgents();
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to duplicate agent', severity: 'error' });
        } finally {
            setDuplicatingId(null);
        }
    };

    // The whole log (config, prompt, live position, latest cycles with their
    // snapshots) is assembled server-side, so this is a single request.
    const handleDownloadLog = async (agent) => {
        setDownloadingLogId(agent.id);
        try {
            const res = await axiosInstance.get(
                `/api/trade-execution/ai-agents/${agent.id}/log/export`,
                { responseType: 'blob' }
            );
            // Prefer the server's filename (agent name + time); CORS can hide the header.
            const disp = res.headers['content-disposition'] || '';
            const match = disp.match(/filename="?([^";]+)"?/);
            saveAs(res.data, match ? match[1] : `ai-agent-log_${agent.id}.log`);
        } catch (err) {
            // responseType 'blob' makes the error body a Blob too - read it back as
            // JSON so a message like "Agent not found" reaches the snackbar.
            let message = 'Failed to download log';
            try {
                message = JSON.parse(await err.response.data.text()).error || message;
            } catch (_) { /* no JSON body - keep the generic message */ }
            setSnackbar({ message, severity: 'error' });
        } finally {
            setDownloadingLogId(null);
        }
    };

    const handleLifecycle = async (id, action) => {
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${id}/${action}`);
            // The backend's message says when a not-yet-started date range begins.
            setSnackbar({ message: res.data?.message || `Agent ${action}ed`, severity: 'success' });
            loadAgents();
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || `Failed to ${action} agent`, severity: 'error' });
        }
    };

    const handleRunCycle = async (id) => {
        setSubmitting(true);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${id}/run-cycle`);
            const data = res.data;
            const decisionCount = data.data?.decisions?.length || 0;
            setSnackbar({ message: data.message || `Cycle completed with ${decisionCount} decisions`, severity: 'success' });
            loadAgents();
            if (selectedAgent?.id === id && activeTab === TAB.DECISIONS) loadDecisions(id);
            if (selectedAgent?.id === id && activeTab === TAB.PERFORMANCE) { loadPerformance(id); loadCycles(id); }
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Cycle failed', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditAgent = (agent) => {
        setAgentEditing(agent);
        setAgentForm({
            name: agent.name || '',
            description: agent.description || '',
            execution_mode: agent.execution_mode || 'NO_LLM',
            symbol: agent.symbol || '',
            symbol_id: agent.symbol_id || '',
            broker_connection_id: agent.broker_connection_id || '',
            temperature: agent.temperature || 0.3,
            max_output_tokens: agent.max_output_tokens || 12000,
            cycle_interval_seconds: agent.cycle_interval_seconds || 30,
            timeframe: agent.config_json?.timeframe
                || secondsToTimeframe(agent.cycle_interval_seconds || 300),
            vwap_timeframe: agent.config_json?.vwap_timeframe || '',
            auto_execute: agent.auto_execute || false,
            capital_allocation: agent.capital_allocation || '',
            max_position_value: agent.max_position_value || '',
            max_daily_loss: agent.max_daily_loss || '',
            max_open_positions: agent.max_open_positions || 3,
            trading_start_time: agent.trading_start_time || '',
            trading_end_time: agent.trading_end_time || '',
            trading_timezone: agent.trading_timezone || 'America/Chicago',
            trading_start_date: agent.trading_start_date || '',
            trading_end_date: agent.trading_end_date || '',
            order_types: {
                buy: agent.config_json?.order_types?.buy || 'LIMIT',
                sell: agent.config_json?.order_types?.sell || 'LIMIT',
            },
            // An agent with no stored block (created before the setting) reads as off.
            order_cancellation: {
                enabled: agent.config_json?.order_cancellation?.enabled === true,
                after_minutes: ORDER_CANCELLATION_MINUTES.includes(Number(agent.config_json?.order_cancellation?.after_minutes))
                    ? Number(agent.config_json.order_cancellation.after_minutes)
                    : ORDER_CANCELLATION_MINUTES[0],
            },
            enabled_agents: {
                macro_strategist: agent.config_json?.enabled_agents?.macro_strategist ?? true,
                options_volatility: agent.config_json?.enabled_agents?.options_volatility ?? true,
                asset_strategist: agent.config_json?.enabled_agents?.asset_strategist ?? true,
            },
        });
        setAgentDialog(true);
    };

    // ==================== PROMPT HANDLERS ====================

    const handleSelectPrompt = (prompt) => {
        setSelectedPrompt(prompt);
        setPromptEditorValue(prompt.system_prompt || '');
        setDataTemplateValue(prompt.data_template || '');
        setPromptName(prompt.name || '');
        setPromptNotes(prompt.notes || '');
    };

    const handleSavePrompt = async () => {
        if (!selectedPrompt || !selectedAgent) return;
        const agentId = selectedAgent.id;
        setSubmitting(true);
        try {
            const edited = {
                name: promptName,
                system_prompt: promptEditorValue,
                data_template: dataTemplateValue,
                notes: promptNotes,
            };
            if (selectedPrompt.source_agent_id) {
                // A prompt from another agent is never edited in place: saving copies it here.
                const copy = await copyPromptIntoAgent(selectedPrompt, edited);
                setSnackbar({ message: `Saved "${copy.name}" as a new version of this agent`, severity: 'success' });
                await loadPrompts(agentId);
                if (selectedAgentIdRef.current === agentId) handleSelectPrompt(copy);
                return;
            }
            await axiosInstance.put(`/api/trade-execution/ai-agents/${agentId}/prompts/${selectedPrompt.id}`, edited);
            setSnackbar({ message: 'Prompt saved', severity: 'success' });
            loadPrompts(agentId);
        } catch (err) {
            setSnackbar({ message: 'Failed to save prompt', severity: 'error' });
            // A copy may exist even though a later step failed: show this agent's real list.
            loadPrompts(agentId);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreatePrompt = async () => {
        if (!selectedAgent) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts`, {
                name: 'New Prompt Version',
            });
            setSnackbar({ message: 'New prompt version created', severity: 'success' });
            loadPrompts(selectedAgent.id);
            handleSelectPrompt(res.data.data);
        } catch (err) {
            setSnackbar({ message: 'Failed to create prompt', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleLoadPresetPrompts = async () => {
        if (!selectedAgent) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts/seed-presets`);
            setSnackbar({ message: res.data.message || 'Preset templates loaded', severity: 'success' });
            loadPrompts(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to load preset templates', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== PROMPT BUILDER — variable insertion & preset copy ====================

    const insertVariableInto = useCallback((field, varName) => {
        const isTemplate = field === 'data_template';
        const insertText = isTemplate ? `{${varName}}` : `\`${varName}\``;
        const ref = isTemplate ? dataTemplateRef : systemPromptRef;
        const el = ref.current;
        const currentValue = isTemplate ? dataTemplateValue : promptEditorValue;
        const setter = isTemplate ? setDataTemplateValue : setPromptEditorValue;

        const selStart = el?.selectionStart ?? currentValue.length;
        const selEnd = el?.selectionEnd ?? currentValue.length;
        const next = currentValue.slice(0, selStart) + insertText + currentValue.slice(selEnd);
        setter(next);

        requestAnimationFrame(() => {
            if (!el) return;
            const caret = selStart + insertText.length;
            el.focus();
            el.setSelectionRange(caret, caret);
        });
    }, [dataTemplateValue, promptEditorValue]);

    const insertVariable = useCallback((varName) => {
        insertVariableInto(focusedField || 'system_prompt', varName);
    }, [focusedField, insertVariableInto]);

    const handleVariableDragStart = (e, varName) => {
        e.dataTransfer.setData('text/variable', varName);
        e.dataTransfer.setData('text/plain', varName);
        e.dataTransfer.effectAllowed = 'copy';
    };

    const handleTextareaDrop = (e, targetField) => {
        const varName = e.dataTransfer.getData('text/variable');
        if (!varName) return; // not a variable drag — let browser default handle
        e.preventDefault();
        setFocusedField(targetField);
        insertVariableInto(targetField, varName);
    };

    const loadPresetTemplates = useCallback(async () => {
        if (presetsLoaded) return;
        try {
            const res = await axiosInstance.get('/api/trade-execution/ai-agents/preset-templates');
            setPresetTemplates(res.data?.data?.presets || []);
            setPresetsLoaded(true);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to load preset templates', severity: 'error' });
        }
    }, [presetsLoaded]);

    const handleOpenPresetMenu = async (e) => {
        setPresetMenuAnchor(e.currentTarget);
        await loadPresetTemplates();
    };

    const handleCopyFromPreset = async (preset) => {
        setPresetMenuAnchor(null);
        if (!selectedAgent || !preset) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts`, {
                name: `${preset.name} (Custom)`,
                system_prompt: preset.system_prompt,
                notes: `Copied from preset: ${preset.name}`,
            });
            setSnackbar({ message: `Copied "${preset.name}" as a new draft`, severity: 'success' });
            await loadPrompts(selectedAgent.id);
            handleSelectPrompt(res.data.data);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to copy preset', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleActivatePrompt = async (promptId) => {
        if (!selectedAgent) return;
        try {
            await axiosInstance.post(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts/${promptId}/activate`);
            setSnackbar({ message: 'Prompt activated', severity: 'success' });
            loadPrompts(selectedAgent.id);
            loadAgents();
        } catch (err) {
            setSnackbar({ message: 'Failed to activate prompt', severity: 'error' });
        }
    };

    // A prompt from another of the owner's agents (`sharedPrompts`) first becomes this
    // agent's own new version, so nothing done here can change the source agent's row.
    const copyPromptIntoAgent = async (prompt, overrides = {}) => {
        const res = await axiosInstance.post(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts`, {
            name: prompt.name,
            system_prompt: prompt.system_prompt,
            data_template: prompt.data_template,
            response_schema_json: prompt.response_schema_json,
            notes: prompt.notes,
            ...overrides,
        });
        return res.data.data;
    };

    const handleActivateSharedPrompt = async (prompt) => {
        if (!selectedAgent) return;
        const agentId = selectedAgent.id;
        setSubmitting(true);
        try {
            const copy = await copyPromptIntoAgent(prompt);
            await axiosInstance.post(`/api/trade-execution/ai-agents/${agentId}/prompts/${copy.id}/activate`);
            setSnackbar({ message: `"${copy.name}" copied from ${prompt.source_agent_name} and activated`, severity: 'success' });
            await loadPrompts(agentId);
            if (selectedAgentIdRef.current === agentId) handleSelectPrompt({ ...copy, version_status: 'ACTIVE' });
            loadAgents();
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to activate the shared prompt', severity: 'error' });
            // If only the activation failed, the copy exists as a draft. Reloading lists it and
            // drops the shared entry, so a retry activates that draft instead of copying again.
            loadPrompts(agentId);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeletePrompt = async (promptId) => {
        if (!selectedAgent) return;
        if (!window.confirm('Are you sure you want to delete this prompt version?')) return;
        try {
            await axiosInstance.delete(`/api/trade-execution/ai-agents/${selectedAgent.id}/prompts/${promptId}`);
            setSnackbar({ message: 'Prompt deleted', severity: 'success' });
            if (selectedPrompt?.id === promptId) setSelectedPrompt(null);
            loadPrompts(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to delete prompt', severity: 'error' });
        }
    };

    // ==================== POSITION SIZING HANDLERS ====================

    const handleSizingMultiplierChange = (idx, raw) => {
        const next = [...sizingMultipliers];
        const num = raw === '' ? 0 : Number(raw);
        next[idx] = Number.isFinite(num) && num >= 0 ? num : 0;
        setSizingMultipliers(next);
        setSizingDirty(true);
    };

    const handleSizingDivisorChange = (raw) => {
        const num = raw === '' ? 0 : Number(raw);
        setSizingDivisor(Number.isFinite(num) ? Math.max(1, Math.floor(num)) : 1);
        setSizingDirty(true);
    };

    const handleSizingReset = () => {
        setSizingDivisor(DEFAULT_POSITION_SIZING.divisor);
        setSizingMultipliers([...DEFAULT_POSITION_SIZING.multipliers]);
        setSizingDirty(true);
    };

    const handleSizingSave = async () => {
        if (!selectedAgent) return;
        if (sizingDivisor <= 0 || sizingMultipliers.length !== 4 || sizingMultipliers.some(m => m < 0)) {
            setSnackbar({ message: 'Divisor must be > 0 and all 4 multipliers must be ≥ 0.', severity: 'error' });
            return;
        }
        const sumMult = sizingMultipliers.reduce((a, b) => a + Number(b || 0), 0);
        if (sumMult > sizingDivisor) {
            setSnackbar({
                message: `Total of all 4 trades (${sumMult}) cannot exceed the Divisor (${sizingDivisor}). That would over-allocate capital.`,
                severity: 'error',
            });
            return;
        }
        setSizingSubmitting(true);
        try {
            const res = await axiosInstance.put(`/api/trade-execution/ai-agents/${selectedAgent.id}`, {
                position_sizing: { divisor: sizingDivisor, multipliers: sizingMultipliers },
            });
            const updated = res?.data?.data;
            if (updated) {
                setSelectedAgent(updated);
                setAgents(prev => prev.map(a => (a.id === updated.id ? updated : a)));
            }
            setSizingDirty(false);
            setSnackbar({ message: 'Position sizing saved', severity: 'success' });
        } catch (err) {
            setSnackbar({
                message: err.response?.data?.message || 'Failed to save position sizing',
                severity: 'error',
            });
        } finally {
            setSizingSubmitting(false);
        }
    };

    // ---- VWAP override ----

    const handleVwapBandChange = (band, raw) => {
        setVwapBands(prev => ({ ...prev, [band]: raw }));
        setVwapDirty(true);
    };

    const handleVwapModeChange = (manual) => {
        if (manual === null) return;   // ToggleButtonGroup fires null on re-click
        setVwapManual(manual);
        setVwapDirty(true);
    };

    const handleVwapSave = async () => {
        if (!selectedAgent) return;
        const parsed = {};
        for (const band of ['upper', 'mid', 'lower']) {
            const raw = String(vwapBands[band] ?? '').trim();
            if (raw === '') continue;    // blank = leave this band on live data
            const num = Number(raw);
            if (!Number.isFinite(num) || num <= 0) {
                setSnackbar({ message: `VWAP ${band} must be a positive number, or left blank to stay live.`, severity: 'error' });
                return;
            }
            parsed[band] = num;
        }
        if (vwapManual && Object.keys(parsed).length === 0) {
            setSnackbar({ message: 'Enter at least one band to use manual VWAP, or switch back to live data.', severity: 'error' });
            return;
        }
        // Only across the bands actually given — a half override has nothing to
        // be inconsistent with. Mirrors the backend check.
        const ordered = ['lower', 'mid', 'upper'].filter(b => b in parsed).map(b => parsed[b]);
        if (ordered.some((v, i) => i > 0 && ordered[i - 1] > v)) {
            setSnackbar({ message: 'VWAP bands must satisfy lower ≤ mid ≤ upper.', severity: 'error' });
            return;
        }
        setVwapSubmitting(true);
        try {
            const res = await axiosInstance.put(`/api/trade-execution/ai-agents/${selectedAgent.id}`, {
                vwap_override: { enabled: vwapManual, ...parsed },
            });
            const updated = res?.data?.data;
            if (updated) {
                setSelectedAgent(updated);
                setAgents(prev => prev.map(a => (a.id === updated.id ? updated : a)));
            }
            setVwapDirty(false);
            // Pull a fresh snapshot so the bands on every sidebar tile and the
            // Live Market Data tab reflect the change immediately.
            loadMarketData(selectedAgent.id);
            setSnackbar({
                message: vwapManual ? 'Manual VWAP saved' : 'VWAP switched back to live data',
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                message: err.response?.data?.message || 'Failed to save VWAP override',
                severity: 'error',
            });
        } finally {
            setVwapSubmitting(false);
        }
    };

    // ==================== RENDER HELPERS ====================

    const renderSkeletonRows = (cols) => (
        [...Array(3)].map((_, i) => (
            <TableRow key={i}>
                {[...Array(cols)].map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" /></TableCell>
                ))}
            </TableRow>
        ))
    );

    // ==================== TAB 0: AGENT MANAGEMENT ====================

    const renderAgentManagement = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                    AI Trading Agents
                </Typography>
                {canAdd && (
                    <Button variant="contained" startIcon={<MuiIcons.Add />} onClick={() => { setAgentEditing(null); setAgentForm({ ...INITIAL_AGENT_FORM }); setAgentDialog(true); }}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                        Create Agent
                    </Button>
                )}
            </Box>
            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={headCellSx}>Name</TableCell>
                            <TableCell sx={headCellSx}>Symbol</TableCell>
                            <TableCell sx={headCellSx}>Status</TableCell>
                            <TableCell sx={headCellSx}>Last Cycle</TableCell>
                            <TableCell sx={headCellSx}>Cycles</TableCell>
                            <TableCell sx={headCellSx}>Decisions</TableCell>
                            <TableCell sx={headCellSx}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {agentsLoading ? renderSkeletonRows(7) : agents.length === 0 ? (
                            <TableRow><TableCell colSpan={7} align="center" sx={bodyCellSx}>No agents created yet</TableCell></TableRow>
                        ) : agents.map((agent) => (
                            <TableRow key={agent.id} hover sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setSelectedAgent(agent);
                                    if (activeTab === TAB.AGENTS) setActiveTab(agent.execution_mode === 'NO_LLM' ? TAB.RULES : TAB.PROMPTS);
                                }}>
                                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{agent.name}</TableCell>
                                <TableCell sx={bodyCellSx}><Chip label={agent.symbol} size="small" variant="outlined" /></TableCell>
                                <TableCell sx={bodyCellSx}>
                                    <Chip label={AIAgentStatusLabels[agent.status] || agent.status}
                                        color={AIAgentStatusColors[agent.status] || 'default'} size="small" />
                                    <TradingDatesCaption agent={agent} />
                                    <WeekendCaption agent={agent} />
                                </TableCell>
                                <TableCell sx={bodyCellSx}>
                                    {agent.last_cycle_at ? new Date(agent.last_cycle_at).toLocaleString() : '-'}
                                </TableCell>
                                <TableCell sx={bodyCellSx}>{agent.total_cycles || 0}</TableCell>
                                <TableCell sx={bodyCellSx}>{agent.total_decisions || 0}</TableCell>
                                <TableCell sx={bodyCellSx} onClick={(e) => e.stopPropagation()}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {agent.status !== 'ACTIVE' && (
                                            <Tooltip title="Start"><IconButton size="small" color="success" onClick={() => handleLifecycle(agent.id, 'start')}><MuiIcons.PlayArrow fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        {agent.status === 'ACTIVE' && (
                                            <Tooltip title="Pause"><IconButton size="small" color="warning" onClick={() => handleLifecycle(agent.id, 'pause')}><MuiIcons.Pause fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        {agent.status !== 'STOPPED' && (
                                            <Tooltip title="Stop"><IconButton size="small" color="error" onClick={() => handleLifecycle(agent.id, 'stop')}><MuiIcons.Stop fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        <Tooltip title="Run Cycle"><IconButton size="small" color="primary" onClick={() => handleRunCycle(agent.id)} disabled={submitting}><MuiIcons.PlayCircleOutline fontSize="small" /></IconButton></Tooltip>
                                        {canEdit && (
                                            <Tooltip title="Edit"><IconButton size="small" onClick={() => handleEditAgent(agent)}><MuiIcons.Edit fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        {/* Creates an agent, so it needs add. The span keeps the Tooltip working while disabled. */}
                                        {canAdd && (
                                            <Tooltip title="Duplicate">
                                                <span>
                                                    <IconButton size="small" onClick={() => handleDuplicateAgent(agent)}
                                                        disabled={duplicatingId === agent.id}>
                                                        {duplicatingId === agent.id
                                                            ? <CircularProgress size={16} />
                                                            : <MuiIcons.ContentCopy fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        {canDelete && (
                                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteAgent(agent.id)}><MuiIcons.Delete fontSize="small" /></IconButton></Tooltip>
                                        )}
                                        {/* Read-only, so not permission-gated: the page already requires view and the
                                            endpoint is team-scoped. The span keeps the Tooltip working while disabled. */}
                                        <Tooltip title="Download Logs">
                                            <span>
                                                <IconButton size="small" color="info" onClick={() => handleDownloadLog(agent)}
                                                    disabled={downloadingLogId === agent.id}>
                                                    {downloadingLogId === agent.id
                                                        ? <CircularProgress size={16} />
                                                        : <MuiIcons.ReceiptLong fontSize="small" />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination component="div" count={agentsTotal} page={agentsPage} rowsPerPage={agentsPerPage}
                    onPageChange={(_, p) => setAgentsPage(p)} onRowsPerPageChange={(e) => { setAgentsPerPage(parseInt(e.target.value)); setAgentsPage(0); }}
                    rowsPerPageOptions={[5, 10, 25]} sx={{ fontFamily }} />
            </TableContainer>
        </Box>
    );

    // ==================== SHARED AGENT SIDEBAR ====================

    // Variables / Position Sizing / Live Market Data, rendered identically on
    // the Prompt Editor, Decision Log and Performance tabs. It stays a render
    // method rather than a component so the three collapse toggles and the
    // sizing draft state live in ONE place and follow the user across tabs -
    // extracting it as a child component would either remount it per tab or
    // need ~20 props threaded through.
    //
    // `canInsert` is true only on the Prompt Editor and the Rule Editor: elsewhere
    // there is nothing to insert into, so the chips are read-only value displays.
    // `ruleMode` is the Rule Editor: the panel then lists the values a rule's formulas
    // may name, and a click puts the bare name into the focused formula box.
    const renderAgentSidebar = (canInsert = false, ruleMode = false) => {
        if (!selectedAgent) return null;
        const isNoLLM = selectedAgent.execution_mode === 'NO_LLM';
        // The built-in router sizes with its own fixed 1-2-4-8 / 15 ladder. A rule reads
        // sizing_t1..t4_shares, which follow this setting, so a rule agent may change it.
        const sizingLocked = isNoLLM && !runsOnRule(selectedAgent);
        const variableGroups = ruleMode
            ? ruleVariableGroups.map((group) => ({
                category: group.group,
                color: RULE_VARIABLE_GROUP_COLORS[group.group] || '#64748b',
                vars: group.vars,
            }))
            : PROMPT_VARIABLES;
        // Same sections, order and strategist gating as the Live Market Data
        // tab - see buildMarketDataSections.
        const sidebarMarketSections = buildMarketDataSections(
            marketData?.categories, resolveEnabledAgents(selectedAgent),
        );
        // Live value per variable name, for the Variables chips.
        const variableValues = buildVariableValueLookup(marketData);
        // Orders still working: why a buy or sell just sent is not in the position
        // values yet. The Position tab shows the same lines.
        const sidebarWorkingLines = workingOrderLines(marketData?.raw);
        // Recent Candles for the Live Market Data panel: the same joined numbers as
        // the tab, and the Timeframe they are on (the text's own header line).
        const sidebarCandleText = joinCandleText(marketData);
        const sidebarCandleTf = /^Timeframe: (\w+)/.exec(marketData?.recent_candles || '')?.[1];

        // Two independently collapsible Papers stacked vertically. The chip list
        // inside Variables has its own maxHeight + internal scroll so it never
        // squeezes Position Sizing off-screen, regardless of how many variable
        // groups are visible. The outer Box scrolls only when both sections
        // together exceed the viewport.
        return (
                <Box sx={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, overflow: 'auto' }}>
                    {/* — Variables Paper (toggle) — */}
                    <Paper sx={{ p: 1.5, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', flexShrink: 0 }}>
                        <Button size="small" onClick={() => {
                                const next = !showVariables;
                                setShowVariables(next);
                                if (next) { setShowSizingEditor(false); setShowVwapOverride(false); setShowLiveMarket(false); }
                            }}
                            startIcon={showVariables ? <MuiIcons.ExpandLess /> : <MuiIcons.ExpandMore />}
                            sx={{ fontFamily, textTransform: 'none', justifyContent: 'flex-start', color: '#1e293b', fontWeight: 600, fontSize: '0.9rem', width: '100%' }}>
                            Variables
                        </Button>
                        <Collapse in={showVariables}>
                            <Box sx={{ mt: 1, maxHeight: '40vh', overflow: 'auto', pr: 0.5 }}>
                                {sidebarWorkingLines.length > 0 && (
                                    <Alert severity="info" icon={false}
                                        sx={{ mb: 1, px: 1, py: 0, fontFamily, fontSize: '0.7rem', lineHeight: 1.4 }}>
                                        <strong>Order working at the broker.</strong> The position values count
                                        the shares that have filled; the rest is not in them yet.
                                        {sidebarWorkingLines.map((line) => (
                                            <Box key={line} sx={{ fontFamily: 'monospace', fontSize: '0.66rem', mt: 0.5, wordBreak: 'break-word' }}>{line}</Box>
                                        ))}
                                    </Alert>
                                )}
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', mb: 1 }}>
                                    {ruleMode ? (
                                        'Click a value to put its name into the formula box you are typing in.'
                                    ) : (
                                        <>Click or drag into the focused field. System Prompt → <code>{'`var`'}</code>, Data Template → <code>{'{var}'}</code>.</>
                                    )}
                                </Typography>
                                <TextField size="small" placeholder="Search variables…" value={variableSearch}
                                    onChange={(e) => setVariableSearch(e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><MuiIcons.Search sx={{ fontSize: '1rem', color: '#94a3b8' }} /></InputAdornment>,
                                    }}
                                    sx={{ mb: 1, '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' } }} />
                                <Box>
                                    {(() => {
                                        const enabledAgents = {
                                            macro_strategist: selectedAgent?.config_json?.enabled_agents?.macro_strategist ?? true,
                                            options_volatility: selectedAgent?.config_json?.enabled_agents?.options_volatility ?? true,
                                            asset_strategist: selectedAgent?.config_json?.enabled_agents?.asset_strategist ?? true,
                                        };
                                        return variableGroups.map((group) => {
                                            const q = variableSearch.trim().toLowerCase();
                                            const filtered = group.vars.filter(v => {
                                                if (v.agent && !enabledAgents[v.agent]) return false;
                                                if (!q) return true;
                                                return v.name.toLowerCase().includes(q) || v.label.toLowerCase().includes(q);
                                            });
                                            if (filtered.length === 0) return null;
                                            return (
                                                <Box key={group.category} sx={{ mb: 1.25 }}>
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                                                        letterSpacing: '0.04em', color: group.color, mb: 0.5, pl: 0.25,
                                                    }}>
                                                        {group.category}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {filtered.map((v) => {
                                                            const entry = variableValues[v.name];
                                                            const shown = compactVariableValue(entry);
                                                            const isNA = entry
                                                                ? isNAFieldValue(entry.value, entry.format) : true;
                                                            // Tooltip carries the label + the UNCLIPPED value.
                                                            const full = entry
                                                                ? String(formatFieldValue(entry.value, entry.format))
                                                                : null;
                                                            // Rule Editor: how the engine reads the value, and for a
                                                            // text field the only labels it ever holds.
                                                            const kind = ruleMode && v.type
                                                                ? `\n${RULE_VARIABLE_TYPE_LABELS[v.type] || v.type}`
                                                                    + (v.values ? `: ${v.values.join(', ')}` : '')
                                                                : '';
                                                            return (
                                                            <Tooltip
                                                                key={v.name}
                                                                placement="top"
                                                                title={(full ? `${v.label} \u2014 ${full}` : v.label) + kind}
                                                                componentsProps={{ tooltip: { sx: {
                                                                    fontFamily, fontSize: '0.72rem', maxWidth: 360,
                                                                    whiteSpace: 'pre-wrap', lineHeight: 1.4,
                                                                } } }}
                                                            >
                                                                <Chip
                                                                    label={(
                                                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.6 }}>
                                                                            <Box component="span">{v.name}</Box>
                                                                            {shown != null && (
                                                                                <Box component="span" sx={{
                                                                                    fontWeight: 700,
                                                                                    color: isNA ? '#ef4444' : '#1e293b',
                                                                                }}>
                                                                                    {shown}
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                    size="small"
                                                                    draggable={canInsert && !ruleMode}
                                                                    // Click + drag insert the variable NAME, never its value.
                                                                    // A formula box takes a click only: the Rule Editor
                                                                    // inserts at the caret of the box that has focus.
                                                                    onDragStart={canInsert && !ruleMode ? (e) => handleVariableDragStart(e, v.name) : undefined}
                                                                    onClick={canInsert ? () => (ruleMode
                                                                        ? ruleEditorRef.current?.insertVariable(v.name)
                                                                        : insertVariable(v.name)) : undefined}
                                                                    sx={{
                                                                        fontFamily: 'monospace', fontSize: '0.7rem', height: 22,
                                                                        cursor: canInsert ? (ruleMode ? 'pointer' : 'grab') : 'default',
                                                                        bgcolor: `${group.color}14`, color: group.color,
                                                                        border: `1px solid ${group.color}30`,
                                                                        '&:hover': { bgcolor: `${group.color}26` },
                                                                        '&:active': { cursor: 'grabbing' },
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            );
                                        });
                                    })()}
                                </Box>
                            </Box>
                        </Collapse>
                    </Paper>

                    {/* — Position Sizing Paper (collapsible) — */}
                    <Paper sx={{ p: 1.5, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', flexShrink: 0 }}>
                        <Button size="small" onClick={() => {
                                const next = !showSizingEditor;
                                setShowSizingEditor(next);
                                if (next) { setShowVariables(false); setShowVwapOverride(false); setShowLiveMarket(false); }
                            }}
                            startIcon={showSizingEditor ? <MuiIcons.ExpandLess /> : <MuiIcons.ExpandMore />}
                            endIcon={sizingDirty ? (
                                <Chip label="unsaved" size="small"
                                    sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#fef3c7', color: '#a16207', fontFamily }} />
                            ) : null}
                            sx={{ fontFamily, textTransform: 'none', justifyContent: 'flex-start', color: '#1e293b', fontWeight: 600, fontSize: '0.9rem', width: '100%' }}>
                            Position Sizing
                        </Button>
                        <Collapse in={showSizingEditor}>
                            <Box sx={{ mt: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#fafbfc' }}>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', mb: 1 }}>
                                    Override the default <strong>1-2-4-8 / 15-part</strong> Martingale ladder. Recomputes
                                    {' '}<code>sizing_t1_shares</code>..<code>sizing_t4_shares</code>
                                    {isNoLLM
                                        ? (sizingLocked
                                            ? '. Read-only here: the built-in router sizes with its own fixed ladder.'
                                            : ", which this agent's rule reads.")
                                        : ' and prefixes the active prompt with an override directive when non-default.'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                    <TextField size="small" type="number" label="Divisor" value={sizingDivisor}
                                        onChange={(e) => handleSizingDivisorChange(e.target.value)}
                                        InputProps={{ readOnly: sizingLocked, inputProps: { min: 1, step: 1 } }}
                                        sx={{ flex: 1, '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontFamily, fontSize: '0.8rem' } }} />
                                    <TextField size="small" label="Current Price"
                                        disabled
                                        value={(() => {
                                            const cp = marketData?.current_price ?? marketData?.raw?.current_price;
                                            return cp != null ? `$${Number(cp).toFixed(2)}` : 'Loading…';
                                        })()}
                                        sx={{ flex: 1, '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontFamily, fontSize: '0.8rem' }, '& .Mui-disabled': { WebkitTextFillColor: '#1e293b !important', color: '#1e293b !important' } }} />
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.68rem', color: '#64748b', mb: 1 }}>
                                    Base Unit U = capital / divisor &nbsp;·&nbsp;
                                    Capital: <strong>${Number(selectedAgent?.capital_allocation || 0).toLocaleString()}</strong>
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                                    {sizingMultipliers.map((mult, idx) => {
                                        const cap = Number(selectedAgent?.capital_allocation || 0);
                                        const baseUnit = sizingDivisor > 0 ? cap / sizingDivisor : 0;
                                        const dollars = baseUnit * Number(mult || 0);
                                        const currentPrice = Number(marketData?.current_price ?? marketData?.raw?.current_price ?? 0);
                                        const isFractional = Boolean(marketData?.raw?.fractional_quantity);
                                        const rawShares = currentPrice > 0 ? dollars / currentPrice : 0;
                                        // Crypto pairs trade fractions, rounded down to 9 decimals like the
                                        // backend's sizing (the epsilon absorbs float error); equities round
                                        // down to whole shares.
                                        const shares = isFractional
                                            ? Math.floor(rawShares * 1e9 + 1e-6) / 1e9
                                            : Math.floor(rawShares);
                                        const sharesDisplay = isFractional
                                            ? shares.toLocaleString(undefined, { maximumFractionDigits: 9 })
                                            : shares.toLocaleString();
                                        return (
                                            <Box key={idx} sx={{ p: 0.75, border: '1px solid #e2e8f0', borderRadius: '6px', bgcolor: '#fff' }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: '#64748b', mb: 0.5 }}>
                                                    Trade {idx + 1}
                                                </Typography>
                                                <TextField size="small" type="number" value={mult}
                                                    onChange={(e) => handleSizingMultiplierChange(idx, e.target.value)}
                                                    InputProps={{ readOnly: sizingLocked, inputProps: { min: 0, step: 0.5 } }}
                                                    fullWidth
                                                    sx={{ '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' } }} />
                                                <Box sx={{ mt: 0.5, fontFamily, fontSize: '0.65rem', color: '#475569', lineHeight: 1.4 }}>
                                                    <div>${dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                                                    <div>{sharesDisplay} {isFractional ? 'units' : 'shares'}</div>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                                {(() => {
                                    const sumMult = sizingMultipliers.reduce((a, b) => a + Number(b || 0), 0);
                                    const overallocated = sumMult > sizingDivisor;
                                    return (
                                        <Box sx={{
                                            mt: 1, p: 0.75, borderRadius: '6px',
                                            bgcolor: overallocated ? '#fef2f2' : '#f1f5f9',
                                            border: `1px solid ${overallocated ? '#fecaca' : '#e2e8f0'}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            fontFamily, fontSize: '0.7rem',
                                        }}>
                                            <span style={{ color: overallocated ? '#b91c1c' : '#475569', fontWeight: 600 }}>
                                                Total of trades: {sumMult} / {sizingDivisor}
                                            </span>
                                            {overallocated && (
                                                <span style={{ color: '#b91c1c' }}>
                                                    Over by {sumMult - sizingDivisor} — reduce a trade or raise the Divisor.
                                                </span>
                                            )}
                                        </Box>
                                    );
                                })()}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75, mt: 1 }}>
                                    <Button size="small" variant="outlined" onClick={handleSizingReset}
                                        disabled={sizingLocked || sizingSubmitting}
                                        sx={{ fontFamily, textTransform: 'none', borderRadius: '8px', fontSize: '0.72rem', px: 1 }}>
                                        Reset
                                    </Button>
                                    <Button size="small" variant="contained" onClick={handleSizingSave}
                                        disabled={sizingLocked || sizingSubmitting || !sizingDirty
                                            || sizingMultipliers.reduce((a, b) => a + Number(b || 0), 0) > sizingDivisor}
                                        sx={{ fontFamily, textTransform: 'none', borderRadius: '8px', fontSize: '0.72rem', px: 1, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                                        {sizingSubmitting ? 'Saving…' : 'Save'}
                                    </Button>
                                </Box>
                            </Box>
                        </Collapse>
                    </Paper>

                    {/* — VWAP Override Paper (collapsible) —
                        Pins vwap_upper / vwap_mid / vwap_lower to values typed by hand
                        instead of the timeframe-scoped live bands. Per-agent, not
                        per-prompt version, so it sits beside Position Sizing rather than
                        inside a prompt. A blank band stays live, so a half override is a
                        first-class case. */}
                    <Paper sx={{ p: 1.5, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', flexShrink: 0 }}>
                        <Button size="small" onClick={() => {
                                const next = !showVwapOverride;
                                setShowVwapOverride(next);
                                if (next) { setShowVariables(false); setShowSizingEditor(false); setShowLiveMarket(false); }
                            }}
                            startIcon={showVwapOverride ? <MuiIcons.ExpandLess /> : <MuiIcons.ExpandMore />}
                            endIcon={vwapDirty ? (
                                <Chip label="unsaved" size="small"
                                    sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#fef3c7', color: '#a16207', fontFamily }} />
                            ) : (selectedAgent?.config_json?.vwap_override?.enabled ? (
                                <Chip label="manual" size="small"
                                    sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#ede9fe', color: '#6d28d9', fontFamily }} />
                            ) : null)}
                            sx={{ fontFamily, textTransform: 'none', justifyContent: 'flex-start', color: '#1e293b', fontWeight: 600, fontSize: '0.9rem', width: '100%' }}>
                            VWAP Override
                        </Button>
                        <Collapse in={showVwapOverride}>
                            <Box sx={{ mt: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#fafbfc' }}>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', mb: 1 }}>
                                    <strong>Live</strong> uses the bands computed on the agent's Timeframe (current
                                    behaviour). <strong>Manual</strong> pins them to fixed values that the LLM prompt,
                                    the Pure Code router and every VWAP display all read.
                                </Typography>
                                <ToggleButtonGroup
                                    exclusive size="small" fullWidth
                                    value={vwapManual ? 'MANUAL' : 'LIVE'}
                                    onChange={(e, v) => handleVwapModeChange(v === null ? null : v === 'MANUAL')}
                                    sx={{ mb: 1, '& .MuiToggleButton-root': { fontFamily, fontSize: '0.72rem', textTransform: 'none', py: 0.4 } }}>
                                    <ToggleButton value="LIVE">Live data</ToggleButton>
                                    <ToggleButton value="MANUAL">Manual</ToggleButton>
                                </ToggleButtonGroup>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {[
                                        { band: 'upper', label: 'VWAP Upper' },
                                        { band: 'mid', label: 'VWAP Mid' },
                                        { band: 'lower', label: 'VWAP Lower' },
                                    ].map(({ band, label }) => (
                                        <TextField key={band} size="small" type="number" label={label}
                                            value={vwapBands[band]}
                                            onChange={(e) => handleVwapBandChange(band, e.target.value)}
                                            disabled={!vwapManual}
                                            placeholder="live"
                                            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                            sx={{
                                                '& .MuiInputBase-root': { fontFamily, fontSize: '0.8rem' },
                                                '& .MuiInputLabel-root': { fontFamily, fontSize: '0.8rem' },
                                            }} />
                                    ))}
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.68rem', color: '#64748b', mt: 0.75 }}>
                                    Leave a band blank to keep that one on live data. Bands must satisfy
                                    {' '}lower &le; mid &le; upper.
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75, mt: 1 }}>
                                    <Button size="small" variant="contained" onClick={handleVwapSave}
                                        disabled={vwapSubmitting || !vwapDirty}
                                        sx={{ fontFamily, textTransform: 'none', borderRadius: '8px', fontSize: '0.72rem', px: 1, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                                        {vwapSubmitting ? 'Saving…' : (vwapManual ? 'Save manual VWAP' : 'Use live data')}
                                    </Button>
                                </Box>
                            </Box>
                        </Collapse>
                    </Paper>

                    {/* -- Live Market Data Paper (collapsible) --
                        The full snapshot the Live Market Data tab renders — every category
                        card, same order, same strategist gating — so a prompt author can see
                        each field they are about to reference without leaving the editor.
                        `marketData` is already refreshed on this tab at the agent's Decision
                        Timing cadence by the effect near loadMarketData, so this panel needs
                        no polling of its own. */}
                    <Paper sx={{ p: 1.5, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', flexShrink: 0 }}>
                        <Button size="small" onClick={() => {
                                const next = !showLiveMarket;
                                setShowLiveMarket(next);
                                if (next) { setShowVariables(false); setShowSizingEditor(false); setShowVwapOverride(false); }
                            }}
                            startIcon={showLiveMarket ? <MuiIcons.ExpandLess /> : <MuiIcons.ExpandMore />}
                            sx={{ fontFamily, textTransform: 'none', justifyContent: 'flex-start', color: '#1e293b', fontWeight: 600, fontSize: '0.9rem', width: '100%' }}>
                            Live Market Data
                        </Button>
                        {/* unmountOnExit: Collapse keeps children mounted by default, and this
                            panel is every category card (~100 rows), the candle numbers and two
                            charts. Without it the whole tree re-renders on each Decision-Timing
                            poll even while the panel is closed. Safe here — the panel's charts
                            take no ref, so they have no live bars to lose on remount. */}
                        <Collapse in={showLiveMarket} unmountOnExit>
                            <Box sx={{ mt: 1, p: 1, border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#fafbfc' }}>
                                {!marketData ? (
                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>
                                        Loading market data...
                                    </Typography>
                                ) : (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b' }}>
                                                {selectedAgent?.symbol}
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>
                                                {(() => {
                                                    const cp = marketData?.current_price ?? marketData?.raw?.current_price;
                                                    return cp != null ? `$${Number(cp).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '-';
                                                })()}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75, mb: 1 }}>
                                            {/* `raw` is the only source of the bands: the SSE stream
                                                writes the price only (it carries a session VWAP, not the
                                                agent's Timeframe), whichever view opened it. The label
                                                names the timeframe the bands were computed on, matching
                                                the Live Market Data tab. */}
                                            {(() => {
                                                const tf = marketData?.raw?.indicator_timeframe;
                                                // 'SESSION' is the stream fallback, not a TimeFrame.
                                                const sfx = tf ? ` (${TimeFrameLabels[tf] || tf})` : '';
                                                // A band pinned in the VWAP Override panel is labelled
                                                // Manual instead of the Timeframe — per band, so a half
                                                // override reads honestly on both halves. Same rule as
                                                // the backend's _build_market_data_categories.
                                                const manual = marketData?.raw?.vwap_override_fields || [];
                                                const bandSfx = (b) => (manual.includes(b) ? ' (Manual)' : sfx);
                                                return [
                                                    { label: `VWAP Upper${bandSfx('upper')}`, value: marketData?.raw?.vwap_upper },
                                                    { label: `VWAP Mid${bandSfx('mid')}`, value: marketData?.raw?.vwap_mid },
                                                    { label: `VWAP Lower${bandSfx('lower')}`, value: marketData?.raw?.vwap_lower },
                                                ];
                                            })().map((f) => (
                                                <Box key={f.label} sx={{ p: 0.6, border: '1px solid #e2e8f0', borderRadius: '6px', bgcolor: '#fff' }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600 }}>
                                                        {f.label}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#334155' }}>
                                                        {f.value != null ? `$${Number(f.value).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '-'}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                        {/* Every category the Live Market Data tab renders, in the same
                                            order and under the same strategist gating — scrolled inside
                                            the panel so the 320px column keeps its own height. */}
                                        {sidebarMarketSections.length > 0 && (
                                            <Box sx={{
                                                maxHeight: 420, overflowY: 'auto', mb: 0.75, pr: 0.4,
                                                '&::-webkit-scrollbar': { width: 6 },
                                                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 4 },
                                            }}>
                                                <CompactMarketDataSections sections={sidebarMarketSections} />
                                            </Box>
                                        )}
                                        {/* Recent Candles, as on the Live Market Data tab: the numbers,
                                            then the Timeframe chart above the 30-second one. Closed
                                            candles only, and neither chart takes a ref. `pre` with its
                                            own scroll: a candle line is wider than the column. */}
                                        {sidebarCandleText && (
                                            <Box sx={{ mb: 0.75 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, mb: 0.4 }}>
                                                    RECENT CANDLES
                                                </Typography>
                                                <Box sx={{
                                                    fontFamily: 'monospace', fontSize: '0.62rem', lineHeight: 1.5, whiteSpace: 'pre',
                                                    color: '#475569', bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px',
                                                    p: 0.75, maxHeight: 220, overflow: 'auto',
                                                    '&::-webkit-scrollbar': { width: 6, height: 6 },
                                                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 4 },
                                                }}>
                                                    {sidebarCandleText}
                                                </Box>
                                            </Box>
                                        )}
                                        {marketData?.recent_candles_data?.length > 0 && (
                                            <Box sx={{ mb: 0.75 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, mb: 0.4 }}>
                                                    {(TimeFrameLabels[sidebarCandleTf] || 'Timeframe').toUpperCase()} CANDLES (CLOSED)
                                                </Typography>
                                                <CandleChart data={marketData.recent_candles_data} height={140} />
                                            </Box>
                                        )}
                                        {marketData?.recent_candles_intra_30s?.length > 0 && (
                                            <Box sx={{ mb: 0.75 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, mb: 0.4 }}>
                                                    LAST 5 MIN (30s BARS)
                                                </Typography>
                                                <CandleChart data={marketData.recent_candles_intra_30s} height={120} secondsVisible />
                                            </Box>
                                        )}
                                        <Typography sx={{ fontFamily, fontSize: '0.62rem', color: '#94a3b8' }}>
                                            {marketData?.agent_timezone ? `Timezone: ${marketData.agent_timezone}` : ''}
                                            {marketData?.timestamp ? ` · ${marketData.timestamp}` : ''}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75, mt: 0.75 }}>
                                            <Button size="small" variant="outlined"
                                                onClick={() => refreshMarketData(selectedAgent.id)}
                                                disabled={marketDataLoading}
                                                sx={{ fontFamily, textTransform: 'none', borderRadius: '8px', fontSize: '0.72rem', px: 1 }}>
                                                {marketDataLoading ? 'Refreshing…' : 'Refresh All'}
                                            </Button>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Collapse>
                    </Paper>
                </Box>
        );
    };

    // Main content beside the shared sidebar. Decision Log and Performance
    // use this so they get the same Variables / Position Sizing / Live Market
    // Data panels as the Prompt Editor, which lays out its own three columns.
    const withAgentSidebar = (content) => (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{content}</Box>
            {renderAgentSidebar(false)}
        </Box>
    );

    // ==================== TAB 1: PROMPT EDITOR ====================

    const renderPromptEditor = () => {
        if (!selectedAgent) return <Alert severity="info" sx={{ fontFamily }}>Select an agent from the Agent Management tab to edit its prompts.</Alert>;

        const isNoLLM = selectedAgent.execution_mode === 'NO_LLM';

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: 'calc(100vh - 280px)' }}>
                {isNoLLM && (
                    <Alert severity="info" icon={<MuiIcons.Lock />} sx={{ fontFamily, fontSize: '0.82rem' }}>
                        This agent runs in <strong>No LLM</strong> mode — trading decisions come from the
                        Pure Code Execution & Risk Router. Prompts are read-only here for reference; saving,
                        activating, creating, and deleting are disabled.
                    </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
                {/* Left panel: prompt versions */}
                <Paper sx={{ width: 280, p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>Versions</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={isNoLLM ? 'Disabled in No LLM mode' : 'Load Preset Strategy Templates'}>
                                <span>
                                    <IconButton size="small" onClick={handleLoadPresetPrompts} disabled={submitting || isNoLLM}>
                                        <MuiIcons.LibraryAdd fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={isNoLLM ? 'Disabled in No LLM mode' : 'Create New Version'}>
                                <span>
                                    <IconButton size="small" onClick={handleCreatePrompt} disabled={submitting || isNoLLM}>
                                        <MuiIcons.Add fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                    {promptsLoading ? <Skeleton variant="rectangular" height={100} /> : prompts.map((p) => (
                        <Paper key={p.id} onClick={() => handleSelectPrompt(p)}
                            sx={{
                                p: 1.5, mb: 1, cursor: 'pointer', borderRadius: '8px',
                                border: `1px solid ${selectedPrompt?.id === p.id ? ACCENT : '#e2e8f0'}`,
                                bgcolor: selectedPrompt?.id === p.id ? `${ACCENT}08` : 'transparent',
                                '&:hover': { borderColor: ACCENT },
                            }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, flex: 1 }}>{p.name}</Typography>
                                {p.version_status !== 'ACTIVE' && !p.is_default && !isNoLLM && (
                                    <Tooltip title="Delete prompt">
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeletePrompt(p.id); }}
                                            sx={{ p: 0.3, color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                            <MuiIcons.DeleteOutline sx={{ fontSize: '0.9rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
                                <Chip label={p.version_label} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                                <Chip label={PromptVersionStatusLabels[p.version_status] || p.version_status}
                                    color={PromptVersionStatusColors[p.version_status] || 'default'} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                            </Box>
                            {p.version_status !== 'ACTIVE' ? (
                                <Button variant="contained" size="small" startIcon={<MuiIcons.CheckCircleOutline sx={{ fontSize: '0.85rem' }} />}
                                    disabled={isNoLLM}
                                    onClick={(e) => { e.stopPropagation(); handleActivatePrompt(p.id); }}
                                    sx={{ fontFamily, textTransform: 'none', fontSize: '0.7rem', mt: 1, borderRadius: '6px',
                                        bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }, px: 1.5, py: 0.3 }}>
                                    Activate
                                </Button>
                            ) : (
                                <Chip icon={<MuiIcons.CheckCircle sx={{ fontSize: '0.8rem' }} />} label="Active"
                                    size="small" sx={{ mt: 1, bgcolor: '#dcfce7', color: '#16a34a', fontSize: '0.7rem', fontWeight: 600, height: 22, fontFamily }} />
                            )}
                        </Paper>
                    ))}
                    {/* Prompts written in this owner's other agents; using one copies it in. */}
                    {!promptsLoading && sharedPrompts.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: '#64748b', mb: 1 }}>
                                From your other agents
                            </Typography>
                            {sharedPrompts.map((p) => (
                                <Paper key={`shared-${p.id}`} onClick={() => handleSelectPrompt(p)}
                                    sx={{
                                        p: 1.5, mb: 1, cursor: 'pointer', borderRadius: '8px',
                                        border: `1px dashed ${selectedPrompt?.id === p.id ? ACCENT : '#cbd5e1'}`,
                                        bgcolor: selectedPrompt?.id === p.id ? `${ACCENT}08` : 'transparent',
                                        '&:hover': { borderColor: ACCENT },
                                    }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600 }}>{p.name}</Typography>
                                    <Chip label={p.source_agent_name} size="small" variant="outlined"
                                        icon={<MuiIcons.SmartToy sx={{ fontSize: '0.8rem' }} />}
                                        sx={{ mt: 0.5, fontSize: '0.65rem', height: 20, maxWidth: '100%' }} />
                                    <Box>
                                        <Button variant="outlined" size="small" disabled={isNoLLM || submitting}
                                            startIcon={<MuiIcons.CheckCircleOutline sx={{ fontSize: '0.85rem' }} />}
                                            onClick={(e) => { e.stopPropagation(); handleActivateSharedPrompt(p); }}
                                            sx={{ fontFamily, textTransform: 'none', fontSize: '0.7rem', mt: 1, borderRadius: '6px',
                                                color: '#16a34a', borderColor: '#22c55e', px: 1.5, py: 0.3 }}>
                                            Activate here
                                        </Button>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    )}
                </Paper>

                {/* Middle panel: editor */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {!selectedPrompt ? (
                        <Alert severity="info" sx={{ fontFamily }}>Select a prompt version from the left panel to edit.</Alert>
                    ) : (
                        <>
                            {selectedPrompt.source_agent_id && (
                                <Alert severity="info" sx={{ fontFamily, fontSize: '0.8rem', mb: 1.5 }}>
                                    From <strong>{selectedPrompt.source_agent_name}</strong>. Save or Activate here copies it
                                    into this agent as a new version; the original is not changed.
                                </Alert>
                            )}
                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <TextField size="small" label="Prompt Name" value={promptName} onChange={(e) => setPromptName(e.target.value)}
                                    InputProps={{ readOnly: isNoLLM }}
                                    sx={{ flex: 1, minWidth: 180, '& .MuiInputBase-root': { fontFamily } }} />
                                <TextField size="small" label="Notes" value={promptNotes} onChange={(e) => setPromptNotes(e.target.value)}
                                    InputProps={{ readOnly: isNoLLM }}
                                    sx={{ flex: 1, minWidth: 180, '& .MuiInputBase-root': { fontFamily } }} />
                                <Button variant="outlined" size="small" onClick={handleOpenPresetMenu} disabled={submitting || isNoLLM}
                                    startIcon={<MuiIcons.ContentCopy sx={{ fontSize: '1rem' }} />}
                                    sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { borderColor: '#7c3aed', bgcolor: '#8b5cf608' } }}>
                                    Copy from Preset
                                </Button>
                                <Menu anchorEl={presetMenuAnchor} open={Boolean(presetMenuAnchor)} onClose={() => setPresetMenuAnchor(null)}>
                                    {presetTemplates.length === 0 ? (
                                        <MenuItem disabled sx={{ fontFamily, fontSize: '0.85rem' }}>
                                            {presetsLoaded ? 'No presets available' : 'Loading presets...'}
                                        </MenuItem>
                                    ) : presetTemplates.map((preset) => (
                                        <MenuItem key={preset.name} onClick={() => handleCopyFromPreset(preset)}
                                            sx={{ fontFamily, fontSize: '0.85rem', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>{preset.name}</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b', whiteSpace: 'normal', maxWidth: 380 }}>
                                                {preset.description}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Menu>
                                <Button variant="contained" onClick={handleSavePrompt} disabled={submitting || isNoLLM}
                                    sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                                    Save
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>System Prompt</Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: focusedField === 'system_prompt' ? ACCENT : '#94a3b8' }}>
                                    {focusedField === 'system_prompt' ? 'Insert target: backticked reference `var`' : 'Click in to set as insert target'}
                                </Typography>
                            </Box>
                            <TextField multiline fullWidth value={promptEditorValue}
                                onChange={(e) => setPromptEditorValue(e.target.value)}
                                onFocus={() => setFocusedField('system_prompt')}
                                inputRef={systemPromptRef}
                                InputProps={{ readOnly: isNoLLM }}
                                inputProps={{
                                    onDragOver: (e) => e.preventDefault(),
                                    onDrop: (e) => handleTextareaDrop(e, 'system_prompt'),
                                }}
                                sx={{
                                    flex: 1, mb: 1,
                                    '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.8rem', alignItems: 'flex-start' },
                                    '& .MuiInputBase-input': { overflow: 'auto' },
                                    '& .MuiOutlinedInput-root': {
                                        ...(focusedField === 'system_prompt' ? { '& fieldset': { borderColor: ACCENT, borderWidth: 2 } } : {}),
                                    },
                                }} />
                            <Button size="small" onClick={() => setShowDataTemplate(!showDataTemplate)}
                                startIcon={showDataTemplate ? <MuiIcons.ExpandLess /> : <MuiIcons.ExpandMore />}
                                sx={{ fontFamily, textTransform: 'none', color: '#64748b', alignSelf: 'flex-start', mb: 0.5 }}>
                                Data Template
                            </Button>
                            <Collapse in={showDataTemplate}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: focusedField === 'data_template' ? ACCENT : '#94a3b8' }}>
                                        {focusedField === 'data_template' ? 'Insert target: placeholder {var}' : 'Click in to set as insert target'}
                                    </Typography>
                                </Box>
                                <TextField multiline fullWidth rows={8} value={dataTemplateValue}
                                    onChange={(e) => setDataTemplateValue(e.target.value)}
                                    onFocus={() => setFocusedField('data_template')}
                                    inputRef={dataTemplateRef}
                                    InputProps={{ readOnly: isNoLLM }}
                                    inputProps={{
                                        onDragOver: (e) => e.preventDefault(),
                                        onDrop: (e) => handleTextareaDrop(e, 'data_template'),
                                    }}
                                    sx={{
                                        '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.8rem' },
                                        '& .MuiOutlinedInput-root': {
                                            ...(focusedField === 'data_template' ? { '& fieldset': { borderColor: ACCENT, borderWidth: 2 } } : {}),
                                        },
                                    }} />
                            </Collapse>
                        </>
                    )}
                </Box>

                {/* Not gated on `selectedPrompt`: the three panels describe the AGENT,
                    not the prompt, and Decision Log / Performance show them with no
                    prompt at all. Only insertion needs an open editor, so that is what
                    `canInsert` tracks - chips stay read-only until a version is picked. */}
                {renderAgentSidebar(!!selectedPrompt)}
                </Box>
            </Box>
        );
    };

    // ==================== TAB: RULE EDITOR (NO_LLM agents) ====================

    // An activation returns the agent with its new active rule. This page's copies
    // follow it, or the sidebar would keep Position Sizing locked for a rule agent.
    const handleRuleAgentUpdated = (updated) => {
        setSelectedAgent(updated);
        setAgents((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    };

    // Same three columns as the Prompt Editor: the list and the editor come from
    // AIAgentRuleEditor, the shared sidebar from this page.
    const renderRuleEditor = () => (
        <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 280px)' }}>
            <AIAgentRuleEditor
                ref={ruleEditorRef}
                agent={selectedAgent}
                canAdd={canAdd}
                canEdit={canEdit}
                canDelete={canDelete}
                onAgentUpdated={handleRuleAgentUpdated}
                onNotify={setSnackbar}
                onCatalogLoaded={setRuleVariableGroups}
            />
            {renderAgentSidebar(true, true)}
        </Box>
    );

    // ==================== TAB: DATA SIMULATOR ====================

    // Keyed by the agent, so nothing of one agent's scenarios or run survives a switch. An
    // inspected bar renders through the same grid as the Decision Log snapshot.
    const renderDataSimulator = () => (
        <AIAgentDataSimulator
            key={selectedAgent.id}
            agent={selectedAgent}
            canAdd={canAdd}
            canEdit={canEdit}
            canDelete={canDelete}
            onNotify={setSnackbar}
            renderSnapshot={(snap) => (
                <MarketDataSectionGrid sections={buildMarketDataSections(snap.categories, resolveEnabledAgents(selectedAgent))} />
            )}
        />
    );

    // ==================== TAB: POSITION ====================

    // The agent's open position. Every number here comes from the blackboard,
    // which derives them in one pass over FILLED orders (`_walk_agent_fills`) -
    // the single definition of "the position" on the backend. Reading it here
    // rather than re-deriving from orders keeps this tab, the LLM prompt and the
    // PureCodeRouter agreeing by construction.
    const renderPosition = () => {
        if (!selectedAgent) return <Alert severity="info" sx={{ fontFamily }}>Select an agent from the Agent Management tab to view its position.</Alert>;

        const raw = marketData?.raw || {};
        const direction = raw.current_position || 'NONE';
        const qty = Number(raw.open_positions || 0);
        const avgCost = Number(raw.equity_avg_cost || 0);
        const price = Number(raw.current_price || 0);
        const ladderDepth = Number(raw.cycle_trades || 0);
        const capital = Number(raw.total_capital || selectedAgent.capital_allocation || 0);
        const isFlat = direction === 'NONE' || qty === 0;

        // Crypto pairs hold fractions of a unit (0.004 BTC); equities whole shares.
        const isFractional = Boolean(raw.fractional_quantity);
        const formatQty = (n) => n.toLocaleString(undefined, { maximumFractionDigits: isFractional ? 9 : 0 });

        // Orders sent but not filled whole yet. Size and avg cost count only what
        // has filled, so the rest of a buy or sell in flight is shown here.
        const workingLines = workingOrderLines(raw);

        const marketValue = qty * price;
        const costBasis = qty * avgCost;
        // Short positions profit when price falls.
        const sign = direction === 'SHORT' ? -1 : 1;
        const unrealized = (avgCost > 0 && price > 0) ? sign * (price - avgCost) * qty : 0;
        const unrealizedPct = costBasis > 0 ? (unrealized / costBasis) * 100 : 0;
        const deployedPct = capital > 0 ? (costBasis / capital) * 100 : 0;

        const dirColor = direction === 'LONG' ? '#10b981' : direction === 'SHORT' ? '#ef4444' : '#94a3b8';
        const pnlColor = unrealized > 0 ? '#10b981' : unrealized < 0 ? '#ef4444' : '#64748b';

        // Martingale ladder: cycle_trades is the depth of the OPEN position, so
        // the next scale-in is tier cycle_trades + 1.
        const tiers = [1, 2, 3, 4].map((n) => ({
            tier: n,
            dollars: Number(raw[`sizing_t${n}_dollars`] || 0),
            shares: Number(raw[`sizing_t${n}_shares`] || 0),
        })).filter((t) => t.dollars > 0 || t.shares > 0);
        const nextTier = ladderDepth + 1;

        const header = (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                    Position - {selectedAgent.name}
                </Typography>
                <Tooltip title="Refresh position">
                    <span>
                        <IconButton size="small" onClick={() => loadMarketData(selectedAgent.id)}
                            disabled={marketDataLoading} sx={{ color: '#3b82f6' }}>
                            <MuiIcons.Refresh fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        );

        // No snapshot of this agent yet: it is on its way, or its load failed. Say that; the
        // numbers below would read "Flat" for a position the page has not been told about.
        if (!marketData?.raw) {
            return (
                <Box>
                    {header}
                    <Alert severity="info" sx={{ mb: 2, fontFamily }}>
                        {marketDataLoading
                            ? 'Loading the position…'
                            : 'The position has not loaded yet. Refresh asks for it again.'}
                    </Alert>
                </Box>
            );
        }

        return (
            <Box>
                {header}

                {workingLines.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2, fontFamily, fontSize: '0.82rem' }}>
                        <strong>Order working at the broker</strong> — sent but not filled whole yet. Size and
                        average cost count the shares that have filled, so they update as it fills.
                        {workingLines.map((line) => (
                            <Box key={line} sx={{ fontFamily: 'monospace', fontSize: '0.78rem', mt: 0.5 }}>{line}</Box>
                        ))}
                    </Alert>
                )}

                {isFlat ? (
                    <Alert severity="info" sx={{ mb: 2, fontFamily }}>
                        <strong>Flat</strong> — no open position. The agent's fills net to zero, so the
                        Martingale ladder is reset to tier 1.
                    </Alert>
                ) : null}

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard icon={MuiIcons.SwapVert} title="Direction" value={direction} color={dirColor} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard icon={MuiIcons.Layers} title="Size" value={formatQty(qty)}
                            color="#3b82f6" subtitle={selectedAgent.symbol} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard icon={MuiIcons.ShoppingCart} title="Avg Cost"
                            value={avgCost > 0 ? `$${avgCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                            color="#8b5cf6" />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard icon={MuiIcons.ShowChart} title="Current Price"
                            value={price > 0 ? `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                            color="#0ea5e9" />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard icon={MuiIcons.AccountBalance} title="Market Value"
                            value={`$${marketValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                            color="#f59e0b" />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <StatCard icon={MuiIcons.TrendingUp} title="Unrealized P&L"
                            value={`${unrealized >= 0 ? '+' : '-'}$${Math.abs(unrealized).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                            color={pnlColor}
                            subtitle={costBasis > 0 ? `${unrealizedPct >= 0 ? '+' : ''}${unrealizedPct.toFixed(2)}%` : undefined} />
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem', color: '#1e293b', mb: 1.5 }}>
                                Capital Deployment
                            </Typography>
                            <DataFieldRow label="Capital Allocation" value={capital} format="currency" />
                            <DataFieldRow label="Cost Basis (deployed)" value={costBasis} format="currency" />
                            <DataFieldRow label="Remaining" value={Math.max(capital - costBasis, 0)} format="currency" />
                            <DataFieldRow label="Deployed %" value={deployedPct} format="percent" />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.95rem', color: '#1e293b', mb: 0.5 }}>
                                Martingale Ladder
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', mb: 1.5 }}>
                                Depth <strong>{ladderDepth}</strong> — entries in the currently open
                                position. Exits never increment it; closing resets it to 0.
                            </Typography>
                            {tiers.length === 0 ? (
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>
                                    No sizing tiers computed yet.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            <TableCell sx={headCellSx}>Tier</TableCell>
                                            <TableCell sx={headCellSx}>Dollars</TableCell>
                                            <TableCell sx={headCellSx}>Shares</TableCell>
                                            <TableCell sx={headCellSx}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tiers.map((t) => (
                                            <TableRow key={t.tier} sx={t.tier === nextTier ? { bgcolor: '#fffbeb' } : undefined}>
                                                <TableCell sx={bodyCellSx}>T{t.tier}</TableCell>
                                                <TableCell sx={bodyCellSx}>${t.dollars.toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                                                <TableCell sx={bodyCellSx}>{formatQty(t.shares)}</TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    {t.tier === nextTier && (
                                                        <Chip label="NEXT" size="small" sx={{
                                                            fontFamily, fontSize: '0.62rem', fontWeight: 700, height: 18,
                                                            bgcolor: '#fef3c7', color: '#b45309',
                                                        }} />
                                                    )}
                                                    {t.tier <= ladderDepth && (
                                                        <Chip label="FILLED" size="small" sx={{
                                                            fontFamily, fontSize: '0.62rem', fontWeight: 700, height: 18,
                                                            bgcolor: '#dcfce7', color: '#15803d',
                                                        }} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    // ==================== TAB 2: DECISION LOG ====================

    const renderDecisionLog = () => {
        if (!selectedAgent) return <Alert severity="info" sx={{ fontFamily }}>Select an agent from the Agent Management tab to view its decisions.</Alert>;

        // Same timezone the agent trades and schedules cycles in, so the log
        // lines up with its trading day rather than with whoever is reading it.
        const agentTz = selectedAgent.trading_timezone || 'America/Chicago';
        // The counts belong to one agent: no cards until this agent's own load lands.
        const summary = decisionSummary?.agentId === selectedAgent.id ? decisionSummary : null;

        const sourceChip = (src) => {
            if (src === 'PURE_CODE_ROUTER') {
                return <Chip label="PURE CODE" size="small" sx={{
                    fontFamily, fontSize: '0.65rem', fontWeight: 700,
                    bgcolor: '#ede9fe', color: '#6d28d9', height: 20,
                }} />;
            }
            if (src === 'RULE_ENGINE') {
                return <Chip label="RULE" size="small" sx={{
                    fontFamily, fontSize: '0.65rem', fontWeight: 700,
                    bgcolor: '#ccfbf1', color: '#0f766e', height: 20,
                }} />;
            }
            if (src === 'LLM') {
                return <Chip label="LLM" size="small" sx={{
                    fontFamily, fontSize: '0.65rem', fontWeight: 700,
                    bgcolor: '#fef3c7', color: '#b45309', height: 20,
                }} />;
            }
            return <Chip label="—" size="small" variant="outlined" sx={{ fontFamily, fontSize: '0.65rem', height: 20 }} />;
        };

        return withAgentSidebar(
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                        Decision Log - {selectedAgent.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Opens the Live Market Data tab's own view in a right drawer. */}
                        <Button variant="outlined" size="small" startIcon={<MuiIcons.DataUsage />}
                            onClick={openLiveDrawer}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: ACCENT, color: ACCENT, whiteSpace: 'nowrap' }}>
                            Live Market Data
                        </Button>
                        <Tooltip title="Refresh decisions">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => loadDecisions(selectedAgent.id)}
                                    disabled={decisionsLoading}
                                    sx={{ color: '#3b82f6' }}
                                >
                                    <MuiIcons.Refresh fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <FormControl size="small" sx={{ minWidth: 220, maxWidth: 320 }}>
                            {/* shrink + displayEmpty: the empty selection renders an "All
                                actions" placeholder, which MUI would otherwise let the
                                floating label sit on top of. */}
                            <InputLabel shrink sx={{ fontFamily }}>Action</InputLabel>
                            <Select
                                multiple
                                displayEmpty
                                notched
                                label="Action"
                                value={decisionsActionFilter}
                                onChange={(e) => {
                                    const picked = e.target.value;
                                    // The "All actions" row is the clear button: selecting it
                                    // empties the list rather than adding a pseudo-value.
                                    setDecisionsActionFilter(
                                        picked.includes(ALL_ACTIONS)
                                            ? [] : picked.filter((v) => v !== ALL_ACTIONS),
                                    );
                                    setDecisionsPage(0);
                                }}
                                renderValue={(picked) => (
                                    picked.length === 0
                                        ? <span style={{ color: '#94a3b8' }}>All actions</span>
                                        : (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                                                {picked.map((a) => (
                                                    <Chip key={a} label={AIAgentDecisionActionLabels[a] || a} size="small"
                                                        sx={{ fontFamily, fontSize: '0.65rem', height: 20 }} />
                                                ))}
                                            </Box>
                                        )
                                )}
                                sx={{ fontFamily }}
                            >
                                <MenuItem value={ALL_ACTIONS} sx={{ fontFamily }}>
                                    <Checkbox size="small" checked={decisionsActionFilter.length === 0} />
                                    All actions
                                </MenuItem>
                                {/* Driven off the shared enum so a new action shows up here
                                    automatically instead of needing a second list. */}
                                {Object.values(AIAgentDecisionAction).map((a) => (
                                    <MenuItem key={a} value={a} sx={{ fontFamily }}>
                                        <Checkbox size="small" checked={decisionsActionFilter.includes(a)} />
                                        {AIAgentDecisionActionLabels[a] || a}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel sx={{ fontFamily }}>Source</InputLabel>
                            <Select
                                label="Source"
                                value={decisionsSourceFilter}
                                onChange={(e) => { setDecisionsSourceFilter(e.target.value); setDecisionsPage(0); }}
                                sx={{ fontFamily }}
                            >
                                <MenuItem value="ALL" sx={{ fontFamily }}>All sources</MenuItem>
                                <MenuItem value="LLM" sx={{ fontFamily }}>LLM only</MenuItem>
                                <MenuItem value="PURE_CODE_ROUTER" sx={{ fontFamily }}>Pure Code only</MenuItem>
                                <MenuItem value="RULE_ENGINE" sx={{ fontFamily }}>Rule only</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                {/* One card per action, from the same response as the rows. The Source
                    filter narrows them; the Action filter does not. They wrap by their own
                    width: this column's width depends on the sidebar, not the viewport. */}
                {summary ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 2, mb: 2 }}>
                        {buildDecisionCards(summary, decisionsSourceFilter).map((card) => (
                            <StatCard key={card.key} icon={card.icon} title={card.title}
                                value={card.value.toLocaleString()} color={card.color} subtitle={card.subtitle} />
                        ))}
                    </Box>
                ) : decisionsLoading && (
                    <Skeleton variant="rectangular" height={96} sx={{ borderRadius: '16px', mb: 2 }} />
                )}
                <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={headCellSx} width={30}></TableCell>
                                <TableCell sx={headCellSx}>
                                    <Tooltip title={`Shown in the agent's trading timezone (${agentTz}), not your local time`}>
                                        <span>Time ({agentTz})</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={headCellSx}>Source</TableCell>
                                <TableCell sx={headCellSx}>Action</TableCell>
                                <TableCell sx={headCellSx}>Symbol</TableCell>
                                <TableCell sx={headCellSx}>Direction</TableCell>
                                <TableCell sx={headCellSx}>Qty</TableCell>
                                <TableCell sx={headCellSx}>Price</TableCell>
                                <TableCell sx={headCellSx}>SL / TP</TableCell>
                                <TableCell sx={headCellSx}>Confidence</TableCell>
                                <TableCell sx={headCellSx}>Phase</TableCell>
                                <TableCell sx={headCellSx}>Executed</TableCell>
                                <TableCell sx={headCellSx} align="center">Snapshot</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {decisionsLoading ? renderSkeletonRows(13) : decisions.length === 0 ? (
                                <TableRow><TableCell colSpan={13} align="center" sx={bodyCellSx}>No decisions yet. Run a cycle to generate decisions.</TableCell></TableRow>
                            ) : decisions.map((d) => (
                                <React.Fragment key={d.id}>
                                    <TableRow hover>
                                        <TableCell sx={bodyCellSx}>
                                            <IconButton size="small" onClick={() => setExpandedDecision(expandedDecision === d.id ? null : d.id)}>
                                                {expandedDecision === d.id ? <MuiIcons.ExpandLess fontSize="small" /> : <MuiIcons.ExpandMore fontSize="small" />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{formatInAgentTz(d.created_at, agentTz)}</TableCell>
                                        <TableCell sx={bodyCellSx}>{sourceChip(d.decision_source)}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip label={AIAgentDecisionActionLabels[d.action] || d.action}
                                                color={AIAgentDecisionActionColors[d.action] || 'default'} size="small" />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{d.symbol}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {d.direction && <Chip label={d.direction} size="small" variant="outlined"
                                                color={d.direction === 'LONG' ? 'success' : 'error'} />}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{d.quantity || '-'}</TableCell>
                                        <TableCell sx={bodyCellSx}>{d.price ? `$${d.price.toFixed(2)}` : '-'}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {d.stop_loss ? `$${d.stop_loss.toFixed(2)}` : '-'} / {d.take_profit ? `$${d.take_profit.toFixed(2)}` : '-'}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {d.confidence_score != null ? (
                                                <Chip label={`${(d.confidence_score * 100).toFixed(0)}%`} size="small" variant="outlined"
                                                    color={d.confidence_score >= 0.7 ? 'success' : d.confidence_score >= 0.4 ? 'warning' : 'error'} />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}><Chip label={d.phase || '-'} size="small" variant="outlined" /></TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {/* Not executed with a reason: red for a broker/runner failure,
                                                amber when the working-order guard held it back. */}
                                            {d.was_executed ? (
                                                <MuiIcons.CheckCircle fontSize="small" color="success" />
                                            ) : d.execution_error ? (
                                                <Tooltip
                                                    title={
                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', whiteSpace: 'pre-wrap', maxWidth: 480 }}>
                                                            {d.execution_error}
                                                        </Typography>
                                                    }
                                                    arrow
                                                    placement="right"
                                                >
                                                    <MuiIcons.ErrorOutline
                                                        fontSize="small"
                                                        sx={{ color: d.execution_status === 'SKIPPED_WORKING_ORDER' ? '#f59e0b' : '#ef4444', cursor: 'help' }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <MuiIcons.RadioButtonUnchecked fontSize="small" color="disabled" />
                                            )}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx} align="center">
                                            <Tooltip title={d.cycle_id
                                                ? 'View the market data this decision was made on'
                                                : 'No cycle linked to this decision'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={!d.cycle_id}
                                                        onClick={() => openDecisionSnapshot(selectedAgent.id, d)}
                                                        sx={{ color: '#0ea5e9' }}
                                                    >
                                                        <MuiIcons.Insights fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={13} sx={{ p: 0, borderBottom: expandedDecision === d.id ? undefined : 'none' }}>
                                            <Collapse in={expandedDecision === d.id}>
                                                <Box sx={{ p: 2, bgcolor: '#f8fafc' }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>Reasoning:</Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#475569', whiteSpace: 'pre-wrap' }}>
                                                        {d.reasoning || 'No reasoning provided'}
                                                    </Typography>
                                                    {d.execution_error && (
                                                        <Alert severity="error" sx={{ mt: 1, fontFamily, fontSize: '0.8rem' }}>{d.execution_error}</Alert>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination component="div" count={decisionsTotal} page={decisionsPage} rowsPerPage={decisionsPerPage}
                        onPageChange={(_, p) => setDecisionsPage(p)} onRowsPerPageChange={(e) => { setDecisionsPerPage(parseInt(e.target.value)); setDecisionsPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]} sx={{ fontFamily }} />
                </TableContainer>
                {renderLiveMarketDrawer()}
            </Box>
        );
    };

    // ==================== TAB 3: PERFORMANCE ====================

    const renderPerformance = () => {
        if (!selectedAgent) return <Alert severity="info" sx={{ fontFamily }}>Select an agent from the Agent Management tab to view performance.</Alert>;

        return withAgentSidebar(
            <Box>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.1rem', color: '#1e293b', mb: 2 }}>
                    Performance - {selectedAgent.name}
                </Typography>

                {performanceLoading ? <Skeleton variant="rectangular" height={120} sx={{ borderRadius: '16px' }} /> : performance && (
                    <>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.Loop} title="Total Cycles" value={performance.total_cycles} color="#3b82f6" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.CheckCircle} title="Success Rate" value={`${performance.cycle_success_rate}%`} color="#10b981" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.Psychology} title="Decisions" value={performance.total_decisions} color="#8b5cf6" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.GppGood} title="Avg Confidence" value={`${(performance.avg_confidence * 100).toFixed(0)}%`} color="#f59e0b" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.AttachMoney} title="LLM Cost" value={`$${performance.total_llm_cost.toFixed(4)}`} color="#ef4444" />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                                <StatCard icon={MuiIcons.Bolt} title="Executed" value={performance.executed_decisions} color="#06b6d4" />
                            </Grid>
                        </Grid>

                        {/* Action Breakdown */}
                        {performance.action_breakdown && Object.keys(performance.action_breakdown).length > 0 && (
                            <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', mb: 1.5 }}>Action Breakdown</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {Object.entries(performance.action_breakdown).map(([action, count]) => (
                                        <Chip key={action} label={`${AIAgentDecisionActionLabels[action] || action}: ${count}`}
                                            color={AIAgentDecisionActionColors[action] || 'default'} variant="outlined" />
                                    ))}
                                </Box>
                            </Paper>
                        )}

                        {/* Phase Breakdown */}
                        {performance.phase_breakdown && Object.keys(performance.phase_breakdown).length > 0 && (
                            <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', mb: 1.5 }}>Phase Breakdown</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {Object.entries(performance.phase_breakdown).map(([phase, count]) => (
                                        <Chip key={phase} label={`${phase}: ${count}`} variant="outlined" />
                                    ))}
                                </Box>
                            </Paper>
                        )}
                    </>
                )}

                {/* Recent Cycles */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem' }}>Recent Cycles</Typography>
                    <Tooltip title="Refresh performance & cycles">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => { loadPerformance(selectedAgent.id); loadCycles(selectedAgent.id); }}
                                disabled={performanceLoading || cyclesLoading}
                                sx={{ color: '#3b82f6' }}
                            >
                                <MuiIcons.Refresh fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={headCellSx}>#</TableCell>
                                <TableCell sx={headCellSx}>Status</TableCell>
                                <TableCell sx={headCellSx}>Started</TableCell>
                                <TableCell sx={headCellSx}>Duration</TableCell>
                                <TableCell sx={headCellSx}>Tokens</TableCell>
                                <TableCell sx={headCellSx}>Cost</TableCell>
                                <TableCell sx={headCellSx}>Model</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cyclesLoading ? renderSkeletonRows(7) : cycles.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={bodyCellSx}>No cycles yet</TableCell></TableRow>
                            ) : cycles.map((c) => (
                                <TableRow key={c.id} hover>
                                    <TableCell sx={bodyCellSx}>{c.cycle_number}</TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Chip label={CycleStatusLabels[c.status] || c.status} color={CycleStatusColors[c.status] || 'default'} size="small" />
                                            {c.error_message && c.status !== 'KILL_SWITCH' && (
                                                <Tooltip
                                                    title={
                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', whiteSpace: 'pre-wrap', maxWidth: 480 }}>
                                                            {c.error_message}
                                                        </Typography>
                                                    }
                                                    arrow
                                                    placement="right"
                                                >
                                                    <MuiIcons.ErrorOutline sx={{ fontSize: 16, color: '#ef4444', cursor: 'help' }} />
                                                </Tooltip>
                                            )}
                                            {c.status === 'KILL_SWITCH' && (() => {
                                                const ks = c.kill_switches_triggered;
                                                const parts = [];
                                                if (ks?.details) parts.push(ks.details);
                                                if (Array.isArray(ks?.list) && ks.list.length > 0) {
                                                    parts.push(`Triggered: ${ks.list.join(', ')}`);
                                                }
                                                if (parts.length === 0 && c.error_message) parts.push(c.error_message);
                                                const tooltipText = parts.join('\n\n') || 'Kill switch triggered (no details recorded)';
                                                return (
                                                    <Tooltip
                                                        title={
                                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', whiteSpace: 'pre-wrap', maxWidth: 480 }}>
                                                                {tooltipText}
                                                            </Typography>
                                                        }
                                                        arrow
                                                        placement="right"
                                                    >
                                                        <MuiIcons.ErrorOutline sx={{ fontSize: 16, color: '#f59e0b', cursor: 'help' }} />
                                                    </Tooltip>
                                                );
                                            })()}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>{c.started_at ? new Date(c.started_at).toLocaleString() : '-'}</TableCell>
                                    <TableCell sx={bodyCellSx}>{c.duration_ms ? `${(c.duration_ms / 1000).toFixed(1)}s` : '-'}</TableCell>
                                    <TableCell sx={bodyCellSx}>{(c.input_tokens || 0) + (c.output_tokens || 0)}</TableCell>
                                    <TableCell sx={bodyCellSx}>{c.total_cost ? `$${c.total_cost.toFixed(4)}` : '-'}</TableCell>
                                    <TableCell sx={bodyCellSx}>{c.llm_model_used || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    // ==================== AGENT DIALOG ====================

    // ==================== TAB 4: LIVE MARKET DATA ====================

    const handleFetchCrypto = async () => {
        setFetchingCrypto(true);
        try {
            await axiosInstance.post('/api/crypto-data/arbitrage/fetch');
            setSnackbar({ message: 'Crypto arbitrage data fetched', severity: 'success' });
            if (selectedAgent) loadMarketData(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to fetch crypto data', severity: 'error' });
        } finally {
            setFetchingCrypto(false);
        }
    };

    const handleFetchL2 = async () => {
        if (!selectedAgent) return;
        setFetchingL2(true);
        try {
            await axiosInstance.post('/api/crypto-data/l2-order-book/fetch', { symbol: selectedAgent.symbol });
            setSnackbar({ message: 'L2 order book data fetched', severity: 'success' });
            loadMarketData(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to fetch L2 data', severity: 'error' });
        } finally {
            setFetchingL2(false);
        }
    };

    const handleFetchMacro = async () => {
        setFetchingMacro(true);
        try {
            await axiosInstance.post('/api/market-data-ingestion/macro/fetch-all');
            setSnackbar({ message: 'Fed macro data fetched successfully', severity: 'success' });
            if (selectedAgent) loadMarketData(selectedAgent.id);
        } catch (err) {
            setSnackbar({ message: err.response?.data?.error || 'Failed to fetch macro data', severity: 'error' });
        } finally {
            setFetchingMacro(false);
        }
    };

    // Decision Log -> Live Market Data drawer. Opening it refreshes the data the way
    // entering the Live Market Data tab does. Called from the click, not from an
    // effect: the per-tab load effect must not re-run when the drawer opens.
    const openLiveDrawer = () => {
        setLiveDrawerOpen(true);
        // Not while a load is in flight: reopening the drawer quickly must not stack
        // refreshes, each of which syncs bars on the server.
        if (!marketDataLoading) refreshMarketData(selectedAgent.id);
    };

    // Also rendered, unchanged, inside the Decision Log's drawer (renderLiveMarketDrawer).
    const renderLiveMarketData = () => {
        if (!selectedAgent) return <Alert severity="info" sx={{ fontFamily }}>Select an agent from the Agent Management tab to view its market data.</Alert>;

        const categories = marketData?.categories || {};
        // One combined block, exactly as this panel showed before the backend
        // split the two halves into separate prompt variables.
        const candleText = joinCandleText(marketData);
        // VWAP bands at every timeframe. Endpoint-only (not on the blackboard),
        // so it is absent from stored cycle snapshots — hence the [] fallback.
        const vwapTimeframes = marketData?.vwap_timeframes || [];
        const vwapOverrideFields = marketData?.raw?.vwap_override_fields || [];

        return (
            <Box>
                {/* Header with actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                            Live Market Data - {selectedAgent.name} ({selectedAgent.symbol})
                        </Typography>
                        {marketData?.timestamp && (
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                Last snapshot: {marketData.timestamp}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" size="small" startIcon={<MuiIcons.CurrencyBitcoin />}
                            onClick={handleFetchCrypto} disabled={fetchingCrypto}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#f59e0b', color: '#f59e0b' }}>
                            {fetchingCrypto ? 'Fetching...' : 'Fetch Crypto Data'}
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<MuiIcons.MenuBook />}
                            onClick={handleFetchL2} disabled={fetchingL2}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#8b5cf6', color: '#8b5cf6' }}>
                            {fetchingL2 ? 'Fetching...' : 'Fetch L2 Order Book'}
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<MuiIcons.AccountBalance />}
                            onClick={handleFetchMacro} disabled={fetchingMacro}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', borderColor: '#059669', color: '#059669' }}>
                            {fetchingMacro ? 'Fetching...' : 'Fetch Fed Macro'}
                        </Button>
                        <Button variant="contained" size="small" startIcon={<MuiIcons.Refresh />}
                            onClick={() => refreshMarketData(selectedAgent.id)} disabled={marketDataLoading}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                            {marketDataLoading ? 'Refreshing...' : 'Refresh All'}
                        </Button>
                    </Box>
                </Box>

                {marketDataLoading && !marketData ? (
                    <Grid container spacing={2}>
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <Grid item xs={12} md={6} lg={4} key={i}>
                                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
                            </Grid>
                        ))}
                    </Grid>
                ) : !marketData ? (
                    <Alert severity="info" sx={{ fontFamily }}>No market data loaded. Click "Refresh All" to load.</Alert>
                ) : (
                    <>
                        {/* Section grouping + strategist gating live in
                            buildMarketDataSections; the Prompt Editor sidebar
                            and the Decision Log snapshot dialog render the
                            exact same set. */}
                        <MarketDataSectionGrid
                            sections={buildMarketDataSections(categories, resolveEnabledAgents(selectedAgent))}
                        />

                        {/* Recent Candles: Numbers + Chart side-by-side
                            (1-hour hybrid window: user-TF for ~55 min + 30s bars for last 5 min).
                            The two halves are separate prompt variables now, so the
                            numbers panel joins them back into one block. */}
                        {(candleText
                            || marketData.recent_candles_data?.length > 0
                            || marketData.recent_candles_intra_30s?.length > 0) && (
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                {/* Left: Candle Numbers */}
                                {candleText && (
                                    <Grid item xs={12} md={6}>
                                        <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', mb: 1, color: '#1e293b' }}>
                                                Recent Candles
                                                {marketData.agent_timezone && (
                                                    <Typography component="span" sx={{ fontFamily, fontWeight: 400, fontSize: '0.7rem', color: '#94a3b8', ml: 1 }}>
                                                        (Timezone: {marketData.agent_timezone})
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Box sx={{
                                                fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap',
                                                color: '#475569', bgcolor: '#f8fafc', p: 1.5, borderRadius: '8px',
                                                maxHeight: 540, overflowY: 'auto',
                                                '&::-webkit-scrollbar': { width: 6 },
                                                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 4 },
                                            }}>
                                                {candleText}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                )}
                                {/* Right: Stacked charts — user-TF on top, 30s tail below */}
                                {(marketData.recent_candles_data?.length > 0
                                    || marketData.recent_candles_intra_30s?.length > 0) && (
                                    <Grid item xs={12} md={6}>
                                        <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                                            {marketData.recent_candles_data?.length > 0 && (
                                                <>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', mb: 1, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        Chart ({selectedAgent?.symbol})
                                                        {/* Live/Stale dot — flips green when SSE events are flowing */}
                                                        <Box component="span" sx={{
                                                            width: 8, height: 8, borderRadius: '50%',
                                                            backgroundColor: streamLive ? '#22c55e' : '#94a3b8',
                                                            boxShadow: streamLive ? '0 0 6px rgba(34,197,94,0.6)' : 'none',
                                                        }} />
                                                        <Typography component="span" sx={{ fontFamily, fontWeight: 500, fontSize: '0.7rem', color: streamLive ? '#16a34a' : '#94a3b8' }}>
                                                            {streamLive ? 'Live' : 'Stale'}
                                                        </Typography>
                                                        {marketData.agent_timezone && (
                                                            <Typography component="span" sx={{ fontFamily, fontWeight: 400, fontSize: '0.7rem', color: '#94a3b8', ml: 'auto' }}>
                                                                (Timezone: {marketData.agent_timezone})
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                    <CandleChart data={marketData.recent_candles_data} height={260} />
                                                </>
                                            )}
                                            {marketData.recent_candles_intra_30s?.length > 0 && (
                                                <Box sx={{ mt: marketData.recent_candles_data?.length > 0 ? 2 : 0, pt: marketData.recent_candles_data?.length > 0 ? 2 : 0, borderTop: marketData.recent_candles_data?.length > 0 ? '1px dashed #e2e8f0' : 'none' }}>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', mb: 1, color: '#1e293b' }}>
                                                        Last 5 Minutes (30s)
                                                        <Typography component="span" sx={{ fontFamily, fontWeight: 400, fontSize: '0.7rem', color: '#94a3b8', ml: 1 }}>
                                                            ({marketData.recent_candles_intra_30s.length} bars)
                                                        </Typography>
                                                    </Typography>
                                                    <CandleChart ref={chartBottomRef} data={marketData.recent_candles_intra_30s} height={160} secondsVisible />
                                                </Box>
                                            )}
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        )}

                        {/* VWAP by timeframe — the whole column (1 Min … Monthly),
                            where the three VWAP tiles above show only the one the
                            agent trades on. Backend: _build_vwap_timeframe_matrix. */}
                        {vwapTimeframes.length > 0 && (
                            <Paper sx={{ mt: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                                <Box sx={{ p: 2, pb: 1 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                        VWAP by Timeframe
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', mt: 0.25 }}>
                                        Live bands at every timeframe. The highlighted row is the one
                                        feeding this agent — change it under Create/Edit Agent → VWAP Timeframe.
                                        {vwapOverrideFields.length > 0 && (
                                            <> The VWAP tiles above are pinned to manual values, so they
                                            will not match this row.</>
                                        )}
                                    </Typography>
                                </Box>
                                <TableContainer sx={{ maxHeight: 420 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={headCellSx}>Timeframe</TableCell>
                                                <TableCell sx={headCellSx} align="right">VWAP Upper</TableCell>
                                                <TableCell sx={headCellSx} align="right">VWAP Mid</TableCell>
                                                <TableCell sx={headCellSx} align="right">VWAP Lower</TableCell>
                                                <TableCell sx={headCellSx} align="right">Band Width</TableCell>
                                                <TableCell sx={headCellSx}>Source</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {vwapTimeframes.map((r) => {
                                                // A timeframe with no usable indicator row reports null
                                                // bands. Show a dash — never a zero, which would read as
                                                // a real price of 0.
                                                const has = r.mid != null;
                                                const width = has && r.upper != null && r.lower != null
                                                    ? r.upper - r.lower : null;
                                                const num = (v) => (v == null ? '—' : v.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2, maximumFractionDigits: 2,
                                                }));
                                                return (
                                                    <TableRow key={r.timeframe} hover
                                                        sx={r.is_selected ? { bgcolor: `${ACCENT}14` } : undefined}>
                                                        <TableCell sx={{ ...bodyCellSx, fontWeight: r.is_selected ? 700 : 500 }}>
                                                            {r.label}
                                                            {r.is_selected && (
                                                                <Chip label="In use" size="small"
                                                                    sx={{
                                                                        ml: 1, height: 18, fontSize: '0.62rem', fontFamily,
                                                                        bgcolor: ACCENT, color: '#fff',
                                                                    }} />
                                                            )}
                                                        </TableCell>
                                                        <TableCell sx={{ ...bodyCellSx, color: has ? '#334155' : '#94a3b8' }} align="right">{num(r.upper)}</TableCell>
                                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: has ? '#1e293b' : '#94a3b8' }} align="right">{num(r.mid)}</TableCell>
                                                        <TableCell sx={{ ...bodyCellSx, color: has ? '#334155' : '#94a3b8' }} align="right">{num(r.lower)}</TableCell>
                                                        <TableCell sx={{ ...bodyCellSx, color: has ? '#334155' : '#94a3b8' }} align="right">{num(width)}</TableCell>
                                                        <TableCell sx={bodyCellSx}>
                                                            {r.source === 'VWAP' ? (
                                                                <Chip label="VWAP" size="small"
                                                                    sx={{ height: 20, fontSize: '0.65rem', fontFamily, bgcolor: '#dcfce7', color: '#15803d' }} />
                                                            ) : r.source === 'BOLLINGER' ? (
                                                                <Tooltip title="No VWAP row at this timeframe — bands derived from Bollinger Bands (20, 2).">
                                                                    <Chip label="Bollinger" size="small"
                                                                        sx={{ height: 20, fontSize: '0.65rem', fontFamily, bgcolor: '#fef3c7', color: '#a16207' }} />
                                                                </Tooltip>
                                                            ) : (
                                                                <Tooltip title="No indicator rows at this timeframe yet. They are provisioned by the market-data ingestion sync.">
                                                                    <Typography component="span" sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>
                                                                        No data
                                                                    </Typography>
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </>
                )}
            </Box>
        );
    };

    // ==================== DECISION LOG: LIVE MARKET DATA DRAWER ====================

    // The Live Market Data tab's own render in a right drawer, so the two cannot
    // differ. Rendered from the Decision Log only: the tab and the drawer are never
    // on screen together, which keeps chartBottomRef on one chart. For the same
    // reason it must never be keepMounted.
    const renderLiveMarketDrawer = () => (
        <Drawer
            anchor="right"
            open={liveDrawerShown}
            onClose={() => setLiveDrawerOpen(false)}
            // Above the app's fixed header, which sits at drawer + 1
            // (components/Header.js) and would cover the top of an ordinary drawer.
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
            PaperProps={{
                sx: {
                    // Everything right of the app's 240px sidebar: with the padding
                    // below, the content is exactly as wide as on the tab, so the card
                    // grid's viewport breakpoints lay the cards out the same.
                    width: { xs: '100%', md: 'calc(100vw - 240px)' },
                    bgcolor: '#f8fafc',
                },
            }}
        >
            {/* Stays in view while the content scrolls under it. */}
            <Box sx={{
                position: 'sticky', top: 0, zIndex: 10, display: 'flex', justifyContent: 'flex-end',
                px: 2, py: 0.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0',
            }}>
                <Tooltip title="Close">
                    <IconButton onClick={() => setLiveDrawerOpen(false)} aria-label="Close Live Market Data">
                        <MuiIcons.Close />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ px: 3, pt: 2, pb: 3 }}>
                {/* The page's own error banner is behind the drawer. */}
                {error && <Alert severity="error" sx={{ mb: 2, fontFamily }} onClose={() => setError(null)}>{error}</Alert>}
                {renderLiveMarketData()}
            </Box>
        </Drawer>
    );

    // ==================== DECISION SNAPSHOT DIALOG ====================

    // Replays the blackboard the agent actually ran on, from the cycle's stored
    // input_data_json. Renders through the same MarketDataSectionGrid the Live
    // Market Data tab uses, so the two views are field-for-field identical.
    const renderSnapshotDialog = () => {
        const entry = snapshotCycle ? snapshotByCycle[snapshotCycle.cycleId] : null;
        const snap = entry?.data || null;
        const d = snapshotCycle?.decision;
        const agentTz = snap?.agent_timezone || selectedAgent?.trading_timezone || 'America/Chicago';
        // Both ride at the top level when there is no snapshot to nest them in.
        const snapshotAt = snap?.snapshot_at || entry?.snapshot_at;
        const cycleNumber = snap?.cycle_number ?? entry?.cycle_number;

        const headline = d
            ? [
                AIAgentDecisionActionLabels[d.action] || d.action,
                d.symbol,
                d.quantity ? `· ${d.quantity}` : null,
                d.price ? `@ $${d.price.toFixed(2)}` : null,
            ].filter(Boolean).join(' ')
            : 'Market Data Snapshot';

        return (
            <Dialog
                open={!!snapshotCycle}
                onClose={closeDecisionSnapshot}
                maxWidth="xl"
                fullWidth
                // lightweight-charts measures its container at init, and inside a
                // Dialog that width is still 0 mid-transition — which renders a
                // collapsed chart. Wait for the transition to finish.
                TransitionProps={{ onEntered: () => setSnapshotChartReady(true) }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 600, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <MuiIcons.Insights sx={{ color: '#0ea5e9' }} />
                        <span>Market Data at Decision Time</span>
                        {d && (
                            <Chip
                                label={headline}
                                size="small"
                                color={AIAgentDecisionActionColors[d.action] || 'default'}
                                sx={{ fontFamily, fontWeight: 700 }}
                            />
                        )}
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b', mt: 0.5 }}>
                        {snapshotAt && `${formatInAgentTz(snapshotAt, agentTz)} (${agentTz})`}
                        {cycleNumber != null && ` · Cycle #${cycleNumber}`}
                        {snap?.cycle_status && ` · ${snap.cycle_status}`}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
                    {snapshotLoading ? (
                        <Grid container spacing={2}>
                            {[1, 2, 3, 4, 5, 6].map(i => (
                                <Grid item xs={12} md={6} lg={4} key={i}>
                                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : !snap ? (
                        <Alert severity="info" sx={{ fontFamily }}>
                            {entry?.reason || 'No market-data snapshot was stored for this cycle.'}
                        </Alert>
                    ) : (
                        <>
                            <MarketDataSectionGrid
                                sections={buildMarketDataSections(snap.categories, resolveEnabledAgents(selectedAgent))}
                            />

                            {snapshotChartReady && snap.recent_candles_data?.length > 0 && (
                                <Paper sx={{ p: 2, mt: 1, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', mb: 1, color: '#1e293b' }}>
                                        Candles the agent saw
                                        <Typography component="span" sx={{ fontFamily, fontWeight: 400, fontSize: '0.7rem', color: '#94a3b8', ml: 1 }}>
                                            ({agentTz})
                                        </Typography>
                                    </Typography>
                                    <CandleChart data={snap.recent_candles_data} height={260} />
                                </Paper>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={closeDecisionSnapshot} sx={{ fontFamily, textTransform: 'none' }}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    // ==================== AGENT DIALOG ====================

    const renderAgentDialog = () => (
        <Dialog open={agentDialog} onClose={() => setAgentDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 600 }}>
                {agentEditing ? 'Edit AI Agent' : 'Create AI Agent'}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Name" size="small" value={agentForm.name}
                            onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Symbol</InputLabel>
                            <Select value={agentForm.symbol_id} label="Symbol"
                                onChange={(e) => {
                                    const sym = watchlistSymbols.find(s => s.id === e.target.value);
                                    const mkt = String(sym?.market_type ?? sym?.asset_class ?? sym?.type ?? '').toUpperCase();
                                    const symText = String(sym?.symbol || '').toUpperCase();
                                    // Crypto tickers + Coinbase (per user classification). Other crypto-adjacent
                                    // equities (MSTR, miners) trade regular NASDAQ hours, so treat as stocks.
                                    const CRYPTO_PROXY_TICKERS = new Set(['COIN']);
                                    const looksCrypto = /(^|[^A-Z])(BTC|ETH|SOL|XRP|DOGE|ADA|BNB|LTC|AVAX|MATIC|DOT)([^A-Z]|$)/.test(symText)
                                        || symText.endsWith('-USD') || symText.endsWith('USDT') || symText.endsWith('USDC')
                                        || CRYPTO_PROXY_TICKERS.has(symText);
                                    const isKnownEquity = mkt === 'NYSE' || mkt === 'NASDAQ' || mkt === 'CBOE' || mkt === 'STOCK' || mkt === 'EQUITY';
                                    const isKnown247 = mkt === 'CRYPTO' || mkt === 'FOREX';
                                    // Unclassified (OTHER / blank) falls back to the ticker heuristic —
                                    // crypto-looking tickers go 24/7, everything else defaults to US equity hours.
                                    const is247 = isKnown247 || (!isKnownEquity && looksCrypto);
                                    const isUsEquity = isKnownEquity || (!isKnown247 && !looksCrypto);
                                    setAgentForm({
                                        ...agentForm,
                                        symbol_id: e.target.value,
                                        symbol: sym?.symbol || '',
                                        trading_start_time: is247 ? '' : (isUsEquity ? '08:30' : agentForm.trading_start_time),
                                        trading_end_time: is247 ? '' : (isUsEquity ? '15:00' : agentForm.trading_end_time),
                                        trading_timezone: isUsEquity ? 'America/Chicago' : (agentForm.trading_timezone || 'America/Chicago'),
                                    });
                                }}
                                sx={{ fontFamily }}>
                                {symbolsLoading ? (
                                    <MenuItem disabled sx={{ fontFamily }}>Loading symbols...</MenuItem>
                                ) : watchlistSymbols.map((s) => (
                                    <MenuItem key={s.id} value={s.id} sx={{ fontFamily }}>
                                        {s.symbol} {s.name ? `- ${s.name}` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '10px',
                            px: 1.5, py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            bgcolor: agentEditing ? '#f8fafc' : '#fff',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                    Execution Mode
                                </Typography>
                                {agentEditing && (
                                    <Tooltip title="Execution mode is locked once an agent is created.">
                                        <MuiIcons.Lock sx={{ fontSize: 16, color: '#94a3b8' }} />
                                    </Tooltip>
                                )}
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b' }}>
                                    {agentForm.execution_mode === 'LLM'
                                        ? 'Trades via the active prompt in Prompt Editor.'
                                        : (!agentEditing
                                            ? 'Starts on the built-in Long Only (Fib Targets) rule. Change it in Rule Editor.'
                                            : (runsOnRule(agentEditing)
                                                ? 'Trades via the active rule in Rule Editor.'
                                                : 'Trades via the built-in Pure Code Router. Activate a rule in Rule Editor to switch.'))}
                                </Typography>
                            </Box>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={agentForm.execution_mode}
                                disabled={!!agentEditing}
                                onChange={(_, v) => { if (v) setAgentForm({ ...agentForm, execution_mode: v }); }}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        fontFamily, textTransform: 'none', px: 2, py: 0.5,
                                        fontSize: '0.78rem', fontWeight: 600,
                                    },
                                    '& .MuiToggleButton-root.Mui-selected': {
                                        bgcolor: ACCENT, color: '#fff',
                                        '&:hover': { bgcolor: '#d97706' },
                                    },
                                }}
                            >
                                <ToggleButton value="NO_LLM">No LLM</ToggleButton>
                                <ToggleButton value="LLM">LLM</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ fontFamily }}>Broker Connection (for auto-execute)</InputLabel>
                            <Select value={agentForm.broker_connection_id || ''} label="Broker Connection (for auto-execute)"
                                onChange={(e) => setAgentForm({ ...agentForm, broker_connection_id: e.target.value })}
                                sx={{ fontFamily }}>
                                <MenuItem value="" sx={{ fontFamily }}>
                                    <em>None (decisions logged only, no orders sent)</em>
                                </MenuItem>
                                {brokerConnectionsLoading ? (
                                    <MenuItem disabled sx={{ fontFamily }}>Loading brokers...</MenuItem>
                                ) : brokerConnections.length === 0 ? (
                                    <MenuItem disabled sx={{ fontFamily }}>No broker connections — create one in Order Management</MenuItem>
                                ) : brokerConnections.map((bc) => (
                                    <MenuItem key={bc.id} value={bc.id} sx={{ fontFamily }}>
                                        {bc.broker_name} {bc.connection_name ? `- ${bc.connection_name}` : ''} {bc.is_paper_trading ? '(Paper)' : '(Live)'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" size="small" multiline rows={2} value={agentForm.description}
                            onChange={(e) => setAgentForm({ ...agentForm, description: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                                Temperature: {agentForm.temperature}
                            </Typography>
                            <Slider value={agentForm.temperature} min={0} max={1} step={0.1}
                                onChange={(_, v) => setAgentForm({ ...agentForm, temperature: v })}
                                sx={{ color: ACCENT }} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Capital Allocation ($)" size="small" type="number" value={agentForm.capital_allocation}
                            onChange={(e) => setAgentForm({ ...agentForm, capital_allocation: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Max Position Value ($)" size="small" type="number" value={agentForm.max_position_value}
                            onChange={(e) => setAgentForm({ ...agentForm, max_position_value: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Max Daily Loss ($)" size="small" type="number" value={agentForm.max_daily_loss}
                            onChange={(e) => setAgentForm({ ...agentForm, max_daily_loss: e.target.value })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small" disabled={!!agentEditing}>
                            <InputLabel>Timeframe</InputLabel>
                            <Select
                                label="Timeframe"
                                value={agentForm.timeframe}
                                onChange={(e) => setAgentForm({ ...agentForm, timeframe: e.target.value })}
                                sx={{ fontFamily }}
                            >
                                {Object.entries(TimeFrame).map(([key, val]) => (
                                    <MenuItem key={val} value={val} sx={{ fontFamily }}>
                                        {TimeFrameLabels[key]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>VWAP Timeframe</InputLabel>
                            <Select
                                label="VWAP Timeframe"
                                value={agentForm.vwap_timeframe}
                                onChange={(e) => setAgentForm({ ...agentForm, vwap_timeframe: e.target.value })}
                                sx={{ fontFamily }}
                            >
                                <MenuItem value="" sx={{ fontFamily }}>Same as Timeframe</MenuItem>
                                {/* THIRTY_SEC is excluded on purpose: IndicatorScheduler
                                    runs a job for every other TimeFrame but not that one,
                                    so picking it could only ever fall forward to a
                                    different timeframe while the label claimed 30 Sec. */}
                                {Object.entries(TimeFrame)
                                    .filter(([key]) => key !== 'THIRTY_SEC')
                                    .map(([key, val]) => (
                                        <MenuItem key={val} value={val} sx={{ fontFamily }}>
                                            {TimeFrameLabels[key]}
                                        </MenuItem>
                                    ))}
                            </Select>
                            <FormHelperText sx={{ fontFamily, fontSize: '0.68rem' }}>
                                VWAP bands only. ATR / CMF / BBW stay on Timeframe.
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Decision Timing (seconds)"
                            size="small"
                            type="number"
                            value={agentForm.cycle_interval_seconds}
                            onChange={(e) => {
                                const n = parseInt(e.target.value, 10);
                                setAgentForm({
                                    ...agentForm,
                                    cycle_interval_seconds: Number.isFinite(n) && n > 0 ? n : '',
                                });
                            }}
                            inputProps={{ min: 1 }}
                            helperText="LLM cycle / Live Market Data refresh cadence — default 30s"
                            sx={{ '& .MuiInputBase-root': { fontFamily } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Max Open Positions" size="small" type="number" value={agentForm.max_open_positions}
                            onChange={(e) => setAgentForm({ ...agentForm, max_open_positions: parseInt(e.target.value) || 3 })}
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Trading Start Date" size="small" type="date" value={agentForm.trading_start_date}
                            onChange={(e) => setAgentForm({ ...agentForm, trading_start_date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            helperText="Blank = trade from today"
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Trading End Date" size="small" type="date" value={agentForm.trading_end_date}
                            onChange={(e) => setAgentForm({ ...agentForm, trading_end_date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: agentForm.trading_start_date || undefined }}
                            helperText="Blank = no end date"
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Trading Start Time" size="small" type="time" value={agentForm.trading_start_time}
                            onChange={(e) => setAgentForm({ ...agentForm, trading_start_time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            helperText="Leave blank for 24/7 (crypto)"
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Trading End Time" size="small" type="time" value={agentForm.trading_end_time}
                            onChange={(e) => setAgentForm({ ...agentForm, trading_end_time: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            helperText="Leave blank for 24/7 (crypto)"
                            sx={{ '& .MuiInputBase-root': { fontFamily } }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Trading Timezone</InputLabel>
                            <Select value={agentForm.trading_timezone} label="Trading Timezone"
                                onChange={(e) => setAgentForm({ ...agentForm, trading_timezone: e.target.value })}
                                sx={{ fontFamily }}>
                                {timezones.map(tz => (
                                    <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <DailyCyclesNote form={agentForm} />
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ p: 1.5, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', mb: 0.25 }}>
                                Strategist Agents
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', mb: 1 }}>
                                This agent will trade using only the selected strategists. Uncheck any strategist to exclude it from this configuration.
                            </Typography>
                            <FormGroup row>
                                <FormControlLabel
                                    control={<Checkbox size="small"
                                        checked={agentForm.enabled_agents.macro_strategist}
                                        onChange={(e) => setAgentForm({
                                            ...agentForm,
                                            enabled_agents: { ...agentForm.enabled_agents, macro_strategist: e.target.checked },
                                        })}
                                        sx={{ '&.Mui-checked': { color: '#059669' } }} />}
                                    label={<Typography sx={{ fontFamily, fontSize: '0.82rem' }}>Macro Strategist Agent</Typography>}
                                />
                                <FormControlLabel
                                    control={<Checkbox size="small"
                                        checked={agentForm.enabled_agents.options_volatility}
                                        onChange={(e) => setAgentForm({
                                            ...agentForm,
                                            enabled_agents: { ...agentForm.enabled_agents, options_volatility: e.target.checked },
                                        })}
                                        sx={{ '&.Mui-checked': { color: '#8b5cf6' } }} />}
                                    label={<Typography sx={{ fontFamily, fontSize: '0.82rem' }}>Options Volatility Agent</Typography>}
                                />
                                <FormControlLabel
                                    control={<Checkbox size="small"
                                        checked={agentForm.enabled_agents.asset_strategist}
                                        onChange={(e) => setAgentForm({
                                            ...agentForm,
                                            enabled_agents: { ...agentForm.enabled_agents, asset_strategist: e.target.checked },
                                        })}
                                        sx={{ '&.Mui-checked': { color: '#f59e0b' } }} />}
                                    label={<Typography sx={{ fontFamily, fontSize: '0.82rem' }}>Asset Strategist Agent</Typography>}
                                />
                            </FormGroup>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ p: 1.5, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', mb: 0.25 }}>
                                Order Types
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b', mb: 1 }}>
                                How this agent's orders are sent to the broker, per side. Limit uses the
                                price on the decision; Market fills at whatever the book offers. A Limit
                                side falls back to Market when the decision carries no price, and
                                Close All / Emergency Exit always flatten at the market.
                            </Typography>
                            {[
                                { side: 'buy', label: 'Buy / Cover' },
                                { side: 'sell', label: 'Sell / Short' },
                            ].map(({ side, label }) => (
                                <Box key={side} sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    gap: 2, py: 0.5,
                                }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#334155' }}>{label}</Typography>
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={agentForm.order_types?.[side] || 'LIMIT'}
                                        onChange={(_, v) => {
                                            // `v` is null when the active button is re-clicked —
                                            // ignore it so a side can never end up unset.
                                            if (!v) return;
                                            setAgentForm({
                                                ...agentForm,
                                                order_types: { ...agentForm.order_types, [side]: v },
                                            });
                                        }}
                                        sx={{
                                            '& .MuiToggleButton-root': {
                                                fontFamily, textTransform: 'none', px: 2, py: 0.4,
                                                fontSize: '0.78rem', fontWeight: 600,
                                            },
                                            '& .MuiToggleButton-root.Mui-selected': {
                                                bgcolor: ACCENT, color: '#fff',
                                                '&:hover': { bgcolor: '#d97706' },
                                            },
                                        }}
                                    >
                                        <ToggleButton value="LIMIT">Limit Order</ToggleButton>
                                        <ToggleButton value="MARKET">Market Order</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ p: 1.5, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', mb: 0.25 }}>
                                        Open Order Cancellation
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b' }}>
                                        Cancel an order at Alpaca when none of it has filled within the chosen
                                        time after it was sent. A partly filled order is left to finish.
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={Boolean(agentForm.order_cancellation?.enabled)}
                                    onChange={(e) => setAgentForm({
                                        ...agentForm,
                                        order_cancellation: { ...agentForm.order_cancellation, enabled: e.target.checked },
                                    })}
                                    inputProps={{ 'aria-label': 'Open Order Cancellation' }}
                                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                />
                            </Box>
                            <FormControl fullWidth size="small" sx={{ mt: 1.25 }} disabled={!agentForm.order_cancellation?.enabled}>
                                <InputLabel sx={{ fontFamily }}>Cancel after</InputLabel>
                                <Select
                                    label="Cancel after"
                                    value={agentForm.order_cancellation?.after_minutes || ORDER_CANCELLATION_MINUTES[0]}
                                    onChange={(e) => setAgentForm({
                                        ...agentForm,
                                        order_cancellation: { ...agentForm.order_cancellation, after_minutes: Number(e.target.value) },
                                    })}
                                    sx={{ fontFamily }}
                                >
                                    {ORDER_CANCELLATION_MINUTES.map((minutes) => (
                                        <MenuItem key={minutes} value={minutes} sx={{ fontFamily }}>{minutes} minutes</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText sx={{ fontFamily }}>
                                    {agentForm.order_cancellation?.enabled
                                        ? `An order still unfilled ${agentForm.order_cancellation.after_minutes} minutes after it was sent is cancelled at Alpaca and here.`
                                        : 'Off: orders wait at the broker until they fill, are cancelled or expire.'}
                                </FormHelperText>
                            </FormControl>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={agentForm.auto_execute} onChange={(e) => setAgentForm({ ...agentForm, auto_execute: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Auto-execute orders (creates orders from AI decisions automatically)</Typography>}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setAgentDialog(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={handleCreateAgent} disabled={submitting || !agentForm.name}
                    sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                    {agentEditing ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== MAIN RENDER ====================

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>You do not have permission to view AI Trading Agents.</Alert>
            </Box>
        );
    }

    // ==================== TAB 6: P&L REPORT ====================

    const renderPnlReport = () => {
        const summary = pnlData?.summary || {};
        const trades = pnlData?.trades || [];
        const pnlColor = (val) => val > 0 ? '#16a34a' : val < 0 ? '#dc2626' : '#64748b';

        return (
            <Box>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.AccountBalance} title="Total P&L" value={`$${(summary.total_pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color={pnlColor(summary.total_pnl)} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.TrendingUp} title="Win Rate" value={`${summary.win_rate || 0}%`} color={(summary.win_rate || 0) >= 50 ? '#16a34a' : '#dc2626'} subtitle={`${summary.winning_trades || 0}W / ${summary.losing_trades || 0}L`} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.SwapVert} title="Total Trades" value={summary.total_trades || 0} color="#6366f1" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.ArrowUpward} title="Best Trade" value={`$${(summary.best_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color="#16a34a" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.ArrowDownward} title="Worst Trade" value={`$${(summary.worst_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color="#dc2626" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StatCard icon={MuiIcons.Functions} title="Avg P&L / Trade" value={`$${(summary.avg_pnl_per_trade || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color={pnlColor(summary.avg_pnl_per_trade)} subtitle={`PF: ${summary.profit_factor || 0}`} />
                    </Grid>
                </Grid>

                {/* Trades Table */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                    {pnlLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rectangular" height={300} sx={{ borderRadius: '12px' }} /></Box>
                    ) : trades.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <MuiIcons.ReceiptLong sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No closed trades yet for this agent</Typography>
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                            <TableCell sx={headCellSx}>Exit Date</TableCell>
                                            <TableCell sx={headCellSx}>Symbol</TableCell>
                                            <TableCell sx={headCellSx}>Side</TableCell>
                                            <TableCell sx={headCellSx} align="right">Entry</TableCell>
                                            <TableCell sx={headCellSx} align="right">Exit</TableCell>
                                            <TableCell sx={headCellSx} align="right">Shares</TableCell>
                                            <TableCell sx={headCellSx} align="right">P&L ($)</TableCell>
                                            <TableCell sx={headCellSx} align="right">P&L (%)</TableCell>
                                            <TableCell sx={headCellSx} align="right">Duration</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {trades.map((t) => (
                                            <TableRow key={t.id} hover>
                                                <TableCell sx={bodyCellSx}>{t.exit_date ? new Date(t.exit_date).toLocaleString() : '—'}</TableCell>
                                                <TableCell sx={bodyCellSx}><strong>{t.symbol}</strong></TableCell>
                                                <TableCell sx={bodyCellSx}>
                                                    <Chip label={t.side} size="small" sx={{ fontFamily, fontWeight: 600, bgcolor: t.side === 'LONG' ? '#dcfce7' : '#fee2e2', color: t.side === 'LONG' ? '#16a34a' : '#dc2626' }} />
                                                </TableCell>
                                                <TableCell sx={bodyCellSx} align="right">${t.entry_price?.toFixed(2)}</TableCell>
                                                <TableCell sx={bodyCellSx} align="right">${t.exit_price?.toFixed(2)}</TableCell>
                                                <TableCell sx={bodyCellSx} align="right">{t.quantity}</TableCell>
                                                <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: pnlColor(t.pnl) }} align="right">
                                                    {t.pnl > 0 ? '+' : ''}${t.pnl?.toFixed(2)}
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, color: pnlColor(t.pnl_pct) }} align="right">
                                                    {t.pnl_pct > 0 ? '+' : ''}{t.pnl_pct?.toFixed(2)}%
                                                </TableCell>
                                                <TableCell sx={bodyCellSx} align="right">
                                                    {t.duration_minutes ? `${Math.floor(t.duration_minutes / 60)}h ${t.duration_minutes % 60}m` : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div" count={pnlTotal} page={pnlPage} rowsPerPage={pnlPerPage}
                                onPageChange={(_, p) => setPnlPage(p)}
                                onRowsPerPageChange={(e) => { setPnlPerPage(parseInt(e.target.value, 10)); setPnlPage(0); }}
                                rowsPerPageOptions={[10, 20, 50]}
                                sx={{ fontFamily, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                            />
                        </>
                    )}
                </Paper>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.5rem', color: '#1e293b' }}>
                    AI Trading Agents
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                    AI-powered quantitative trading agents using Gemini LLM for automated analysis and execution
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Selected Agent Indicator */}
            {selectedAgent && activeTab >= FIRST_AGENT_TAB && (
                <Alert severity="info" sx={{ mb: 2, fontFamily, '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 1 } }}
                    action={<Button size="small" onClick={() => { setSelectedAgent(null); setActiveTab(TAB.AGENTS); }} sx={{ fontFamily, textTransform: 'none' }}>Change</Button>}>
                    Viewing: <strong>{selectedAgent.name}</strong> ({selectedAgent.symbol})
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                {/* Scrollable: with the Data Simulator the tabs overflow a 1440 px screen, and the
                    standard variant hid Agent Management with no way to reach it. */}
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable" scrollButtons="auto"
                    sx={{
                        '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 500, minHeight: 48 },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT },
                    }}>
                    <Tab value={TAB.AGENTS} label="Agent Management" icon={<MuiIcons.SmartToy sx={{ fontSize: 18 }} />} iconPosition="start" />
                    <Tab value={TAB.CONNECT} label="Connect Account" icon={<MuiIcons.Cable sx={{ fontSize: 18 }} />} iconPosition="start" />
                    {selectedAgent && selectedAgent.execution_mode !== 'NO_LLM' && <Tab value={TAB.PROMPTS} label="Prompt Editor" icon={<MuiIcons.Code sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && selectedAgent.execution_mode === 'NO_LLM' && <Tab value={TAB.RULES} label="Rule Editor" icon={<MuiIcons.Rule sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && <Tab value={TAB.POSITION} label="Position" icon={<MuiIcons.AccountBalanceWallet sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && <Tab value={TAB.DECISIONS} label="Decision Log" icon={<MuiIcons.ListAlt sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && <Tab value={TAB.PERFORMANCE} label="Performance" icon={<MuiIcons.BarChart sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && <Tab value={TAB.MARKET_DATA} label="Live Market Data" icon={<MuiIcons.DataUsage sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && <Tab value={TAB.PNL} label="P&L Report" icon={<MuiIcons.Assessment sx={{ fontSize: 18 }} />} iconPosition="start" />}
                    {selectedAgent && <Tab value={TAB.SIMULATOR} label="Data Simulator" icon={<MuiIcons.Science sx={{ fontSize: 18 }} />} iconPosition="start" />}
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === TAB.AGENTS && renderAgentManagement()}
            {activeTab === TAB.CONNECT && <ConnectAccountTab canAdd={canAdd} canEdit={canEdit} canDelete={canDelete} onConnectionsChange={fetchBrokerConnections} />}
            {selectedAgent && activeTab === TAB.PROMPTS && renderPromptEditor()}
            {selectedAgent && activeTab === TAB.RULES && selectedAgent.execution_mode === 'NO_LLM' && renderRuleEditor()}
            {selectedAgent && activeTab === TAB.POSITION && renderPosition()}
            {selectedAgent && activeTab === TAB.DECISIONS && renderDecisionLog()}
            {selectedAgent && activeTab === TAB.PERFORMANCE && renderPerformance()}
            {selectedAgent && activeTab === TAB.MARKET_DATA && renderLiveMarketData()}
            {selectedAgent && activeTab === TAB.PNL && renderPnlReport()}
            {selectedAgent && activeTab === TAB.SIMULATOR && renderDataSimulator()}

            {/* Dialogs */}
            {renderAgentDialog()}
            {renderSnapshotDialog()}

            {/* Snackbar */}
            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                {snackbar && <Alert severity={snackbar.severity} sx={{ fontFamily }}>{snackbar.message}</Alert>}
            </Snackbar>
        </Box>
    );
};

export default AIAgentPage;
